import React, { useEffect, useState, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users, Calendar, LayoutDashboard, Tag, Shield, LogOut, Plus, Search, Filter, ChevronRight, MapPin, Activity, Download, Copy, MessageCircle, Send, Key, Trash2, Edit, Menu, X, ClipboardList } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// CPI(M) official party emblem (hosted asset)
const CPIM_LOGO_URL = "https://customer-assets.emergentagent.com/job_mapping-portal-2/artifacts/efwq32bp_Communist-Party-of-India-Marxist-LOGO.jpg";

function CPIMLogo({ className = "w-6 h-6" }) {
  return (
    <img
      src={CPIM_LOGO_URL}
      alt="CPI(M) emblem"
      className={`${className} object-contain`}
      draggable={false}
    />
  );
}

// --- I18n ---
const TR = {
  en: {
    app: "Local Influencer Database", loginTitle: "Secure Administrator Login", email: "Email", password: "Password", login: "Sign In",
    dashboard: "Dashboard", influencers: "Influencers", events: "Events", categories: "Categories", audit: "Audit Log", users: "Users",
    logout: "Sign Out", welcome: "Welcome", totalInfluencers: "Total Influencers", totalEvents: "Total Events",
    participations: "Participations", unengaged: "Unengaged", byCategory: "By Category", byDistrict: "By District", byAC: "By Assembly Constituency",
    eventsByType: "Events by Type", recentEvents: "Recent Events", search: "Search…", name: "Name", contact: "Contact", district: "District",
    profession: "Profession", category: "Category", subcategory: "Subcategory", action: "Actions", add: "Add", edit: "Edit",
    save: "Save", cancel: "Cancel", delete: "Delete", ac: "Assembly Constituency", municipality: "Municipality", panchayat: "Panchayat",
    ward: "Ward", village: "Village", eventName: "Event Name", eventType: "Event Type", eventDate: "Event Date", venue: "Venue",
    description: "Description", filter: "Filters", status: "Status", linkInfluencer: "Link Influencer", noResults: "No records found.",
    createInfluencer: "New Influencer", createEvent: "New Event", basicInfo: "Basic Info", locality: "Locality", influence: "Influence & Skills",
    ageGroup: "Age Group", gender: "Gender", skillArea: "Skill / Area", profileBrief: "Brief Profile", engagementStatus: "Engagement Status",
    role: "Role", createUser: "Add User", allDistricts: "All Districts", allACs: "All ACs", allCategories: "All Categories",
    invitationStatus: "Invitation", involvement: "Involvement", nature: "Nature", feedback: "Feedback", followUp: "Follow-up",
    parentCategory: "Parent Category", addCategory: "Add Category", none: "None", timestamp: "Timestamp", user: "User", target: "Target",
    details: "Details", organisingUnit: "Organising Unit", estimatedAttendance: "Est. Attendance", follow_up_required: "Follow-up required",
    photo: "Photograph", uploadPhoto: "Upload Photo", subtitle: "Party-internal Command Centre — Restricted Access",
    invitations: "WhatsApp Invitations", englishMsg: "English Message", bengaliMsg: "Bengali Message", copy: "Copy", openWhatsApp: "Open WhatsApp", whatsapp: "WhatsApp", copied: "Copied to clipboard", export: "Export CSV",
    resetPassword: "Reset Password", newPassword: "New Password", close: "Close", confirmDelete: "Are you sure you want to delete this?",
    exportExcel: "Export Excel",
    changePassword: "Change Password", currentPassword: "Current Password", confirmPassword: "Confirm Password", forgotPassword: "Forgot Password?", sendResetLink: "Send Reset Link", backToLogin: "Back to Login",
    baithakRecords: "Para Baithak Records", newBaithak: "New Baithak", editBaithak: "Edit Baithak", noBaithaks: "No baithak records yet.",
    timeLocation: "Time & Location", dateTime: "Date & Time", paraName: "Para (Meeting) Name", localBodyType: "Local Body Type", gramPanchayat: "Gram Panchayat",
    wardOptional: "Ward No (optional)", villageOptional: "Village (optional)", paraOptional: "Para / Neighbourhood (optional)", paraPlaceholder: "e.g. Uttar Para",
    specifyMunicipality: "Specify municipality name", specifyPanchayat: "Specify Panchayat name", other: "Other",
    attendance: "Attendance", maleCount: "Male Count", femaleCount: "Female Count", total: "Total",
    attendeeDetails: "Attendee Details", attendeeSex: "Sex", attendeePhone: "Phone", socialStatus: "Social Status",
    minutesAndPhoto: "Minutes & Photo", meetingMinutes: "Meeting Minutes", meetingPhoto: "Meeting Photo",
    takePhoto: "Take Photo (Camera)", fromGallery: "From Gallery", captureLiveLocation: "Capture Live Location", capturing: "Capturing…",
    locationCaptured: "Location captured", removePhoto: "Remove photo", photoTip: "Tip: on mobile 'Take Photo' opens the camera directly. Location needs one-time browser permission.",
    saveBaithak: "Save Baithak", backToPortal: "Back to Portal",
    manageUsers: "Manage Users", addUser: "Add User",
    para: "Para", localBody: "Local Body", by: "By", recordedBy: "Recorded By", photograph: "Photograph", location: "Location", openInMaps: "Open in Maps",
    portalTitle: "One centralized database.", portalTitle2: "Every electoral insight.",
    portalSub: "Social demographics, historical voting patterns, and constituency mapping for the CPI(M) West Bengal cadre — accessible from a single secure gateway.",
    chooseTool: "Choose a tool", enter: "Enter", open: "Open",
    tileLIDtitle: "Local Influencer Database",
    tileLIDsub: "Track and engage local influencers across every district",
    tileLIDdesc: "Manage influencer profiles, event participation, and outreach across all 20 West Bengal districts.",
    tileLIDaccent: "Restricted · Party Internal",
    tileSIRtitle: "SIR",
    tileSIRsub: "Special Intensive Revision — deleted voters",
    tileSIRdesc: "Explore SIR deletion data across constituencies with breakdowns by gender, community and geography.",
    tileSIRaccent: "Data Explorer",
    tileBoothtitle: "Booth Local Body Mapping",
    tileBoothsub: "Booth-wise Panchayat / Municipality allocation",
    tileBoothdesc: "Map every polling booth to its Gram Panchayat or Municipal ward for organisational planning.",
    tileBoothaccent: "Organisational Tool",
    tileForm20title: "Form20 WestBengal 2011 to 2026",
    tileForm20sub: "Booth-level results — 2011 · 2014 · 2016 · 2019 · 2021 · 2024 · 2026",
    tileForm20desc: "Explore Form 20 booth-wise polled vote counts across every West Bengal state and Lok Sabha election from 2011 onwards.",
    tileForm20accent: "Election Data",
    tileDemographytitle: "Booth Demography",
    tileDemographysub: "Booth-wise demographic breakdown",
    tileDemographydesc: "Explore booth-level demographic composition — gender, age and community — across every assembly constituency.",
    tileDemographyaccent: "Data Explorer",
    tileMuniResultsTitle: "Municipality Results & Demography",
    tileMuniResultssub: "Ward-wise 2026 Vidhan Sabha results with demographic overlays",
    tileMuniResultsdesc: "Explore every West Bengal municipal body ward by ward — vote share, demographics and turnout from the 2026 state election.",
    tileMuniResultsaccent: "Election + Demography",
    tileSurveyTitle: "Municipal Surveys",
    tileSurveysub: "Door-to-door data collection · KMC · HMC · AMC",
    tileSurveydesc: "Field workers collect ward-level responses across Kolkata, Howrah and Asansol Municipal Corporations.",
    tileSurveyaccent: "Field Survey · Public",
    tileSurveyAdmin: "Survey Admin",
    tileMediaTitle: "Media Watch",
    tileMediasub: "Daily monitor of West Bengal news coverage",
    tileMediadesc: "Track how the state and national press cover West Bengal — headlines, framing and highlights curated for organisers.",
    tileMediaaccent: "News Monitor",
    tileBaithakTitle: "Para Baithak",
    tileBaithaksub: "Record local body meetings with minutes and attendees",
    tileBaithakdesc: "Log every para baithak — attendees, minutes, and location-tagged photos — across the state.",
    tileBaithakaccent: "Field Reporting",
    authorisedOnly: "Authorised organisers only",
  },
  bn: {
    app: "স্থানীয় প্রভাবশালী ডাটাবেস", loginTitle: "সুরক্ষিত প্রশাসক লগইন", email: "ইমেল", password: "পাসওয়ার্ড", login: "প্রবেশ করুন",
    dashboard: "ড্যাশবোর্ড", influencers: "প্রভাবশালী", events: "অনুষ্ঠান", categories: "শ্রেণী", audit: "অডিট লগ", users: "ব্যবহারকারী",
    logout: "বেরিয়ে যান", welcome: "স্বাগতম", totalInfluencers: "মোট প্রভাবশালী", totalEvents: "মোট অনুষ্ঠান",
    participations: "অংশগ্রহণ", unengaged: "নিষ্ক্রিয়", byCategory: "শ্রেণী অনুযায়ী", byDistrict: "জেলা অনুযায়ী", byAC: "বিধানসভা অনুযায়ী",
    eventsByType: "ধরন অনুযায়ী অনুষ্ঠান", recentEvents: "সাম্প্রতিক অনুষ্ঠান", search: "খুঁজুন…", name: "নাম", contact: "যোগাযোগ", district: "জেলা",
    profession: "পেশা", category: "শ্রেণী", subcategory: "উপ-শ্রেণী", action: "ক্রিয়া", add: "যোগ", edit: "সম্পাদনা",
    save: "সংরক্ষণ", cancel: "বাতিল", delete: "মুছুন", ac: "বিধানসভা কেন্দ্র", municipality: "পৌরসভা", panchayat: "পঞ্চায়েত",
    ward: "ওয়ার্ড", village: "গ্রাম", eventName: "অনুষ্ঠানের নাম", eventType: "অনুষ্ঠানের ধরন", eventDate: "তারিখ", venue: "স্থান",
    description: "বিবরণ", filter: "ফিল্টার", status: "অবস্থা", linkInfluencer: "প্রভাবশালী যুক্ত করুন", noResults: "কোনো রেকর্ড নেই।",
    createInfluencer: "নতুন প্রভাবশালী", createEvent: "নতুন অনুষ্ঠান", basicInfo: "প্রাথমিক তথ্য", locality: "অঞ্চল", influence: "প্রভাব ও দক্ষতা",
    ageGroup: "বয়সের গ্রুপ", gender: "লিঙ্গ", skillArea: "দক্ষতা / ক্ষেত্র", profileBrief: "সংক্ষিপ্ত পরিচিতি", engagementStatus: "অংশগ্রহণ অবস্থা",
    role: "ভূমিকা", createUser: "ব্যবহারকারী যোগ", allDistricts: "সব জেলা", allACs: "সব বিধানসভা", allCategories: "সব শ্রেণী",
    invitationStatus: "আমন্ত্রণ", involvement: "সংযুক্তি", nature: "ধরন", feedback: "মতামত", followUp: "অনুবর্তন",
    parentCategory: "মূল শ্রেণী", addCategory: "শ্রেণী যোগ", none: "কিছু নেই", timestamp: "সময়", user: "ব্যবহারকারী", target: "লক্ষ্য",
    details: "বিবরণ", organisingUnit: "আয়োজক ইউনিট", estimatedAttendance: "উপস্থিতি", follow_up_required: "অনুবর্তন প্রয়োজন",
    photo: "ছবি", uploadPhoto: "ছবি আপলোড", subtitle: "দল-অভ্যন্তরীণ কমান্ড সেন্টার — সীমিত প্রবেশাধিকার",
    invitations: "হোয়াটসঅ্যাপ আমন্ত্রণ", englishMsg: "ইংরেজি বার্তা", bengaliMsg: "বাংলা বার্তা", copy: "কপি", openWhatsApp: "হোয়াটসঅ্যাপ খুলুন", whatsapp: "হোয়াটসঅ্যাপ", copied: "ক্লিপবোর্ডে কপি হয়েছে", export: "CSV এক্সপোর্ট",
    resetPassword: "পাসওয়ার্ড রিসেট", newPassword: "নতুন পাসওয়ার্ড", close: "বন্ধ করুন", confirmDelete: "আপনি কি সত্যিই মুছে ফেলতে চান?",
    exportExcel: "এক্সেল এক্সপোর্ট",
    changePassword: "পাসওয়ার্ড পরিবর্তন", currentPassword: "বর্তমান পাসওয়ার্ড", confirmPassword: "পাসওয়ার্ড নিশ্চিত করুন", forgotPassword: "পাসওয়ার্ড ভুলে গেছেন?", sendResetLink: "রিসেট লিঙ্ক পাঠান", backToLogin: "লগইনে ফিরে যান",
    baithakRecords: "পাড়া বৈঠক তালিকা", newBaithak: "নতুন বৈঠক", editBaithak: "বৈঠক সম্পাদনা", noBaithaks: "এখনও কোনো বৈঠকের তথ্য নেই।",
    timeLocation: "সময় ও স্থান", dateTime: "তারিখ ও সময়", paraName: "পাড়া (বৈঠকের নাম)", localBodyType: "স্থানীয় সংস্থার ধরন", gramPanchayat: "গ্রাম পঞ্চায়েত",
    wardOptional: "ওয়ার্ড নং (ঐচ্ছিক)", villageOptional: "গ্রাম (ঐচ্ছিক)", paraOptional: "পাড়া / এলাকা (ঐচ্ছিক)", paraPlaceholder: "যেমন উত্তর পাড়া",
    specifyMunicipality: "পৌরসভার নাম লিখুন", specifyPanchayat: "পঞ্চায়েতের নাম লিখুন", other: "অন্যান্য",
    attendance: "উপস্থিতি", maleCount: "পুরুষ", femaleCount: "মহিলা", total: "মোট",
    attendeeDetails: "উপস্থিতদের বিবরণ", attendeeSex: "লিঙ্গ", attendeePhone: "ফোন", socialStatus: "সামাজিক অবস্থান",
    minutesAndPhoto: "কার্যবিবরণী ও ছবি", meetingMinutes: "সভার কার্যবিবরণী", meetingPhoto: "সভার ছবি",
    takePhoto: "ছবি তুলুন (ক্যামেরা)", fromGallery: "গ্যালারি থেকে", captureLiveLocation: "লাইভ লোকেশন নিন", capturing: "নিচ্ছে…",
    locationCaptured: "লোকেশন নেওয়া হয়েছে", removePhoto: "ছবি সরান", photoTip: "টিপ: মোবাইলে 'ছবি তুলুন' সরাসরি ক্যামেরা খোলে। লোকেশনের জন্য একবার ব্রাউজার অনুমতি লাগবে।",
    saveBaithak: "বৈঠক সংরক্ষণ", backToPortal: "পোর্টালে ফিরে যান",
    manageUsers: "ব্যবহারকারী পরিচালনা", addUser: "ব্যবহারকারী যোগ",
    para: "পাড়া", localBody: "স্থানীয় সংস্থা", by: "লিখেছেন", recordedBy: "রেকর্ড করেছেন", photograph: "ছবি", location: "স্থান", openInMaps: "মানচিত্রে দেখুন",
    portalTitle: "একটি কেন্দ্রীভূত ডাটাবেস।", portalTitle2: "প্রতিটি নির্বাচনী দৃষ্টিভঙ্গি।",
    portalSub: "সামাজিক জনতাত্ত্বিক তথ্য, ঐতিহাসিক ভোটের প্রবণতা এবং কেন্দ্রভিত্তিক ম্যাপিং — সিপিআই(এম) পশ্চিমবঙ্গের কর্মীদের জন্য একটি সুরক্ষিত প্রবেশদ্বার।",
    chooseTool: "একটি টুল বেছে নিন", enter: "প্রবেশ", open: "খুলুন",
    tileLIDtitle: "স্থানীয় বিশিষ্ট ব্যক্তিদের তথ্যভাণ্ডার",
    tileLIDsub: "প্রতিটি জেলার স্থানীয় প্রভাবশালীদের ট্র্যাক ও যুক্ত করুন",
    tileLIDdesc: "সমস্ত ২৩টি পশ্চিমবঙ্গ জেলার প্রভাবশালী, অনুষ্ঠান ও যোগাযোগ পরিচালনা করুন।",
    tileLIDaccent: "সীমিত · দল-অভ্যন্তরীণ",
    tileSIRtitle: "SIR",
    tileSIRsub: "স্পেশাল ইনটেনসিভ রিভিশন — বাদ পড়া ভোটার",
    tileSIRdesc: "সিআর ডিলিশন ডেটা বিধানসভা কেন্দ্র অনুযায়ী দেখুন — লিঙ্গ, সম্প্রদায় ও ভৌগোলিক বিভাজন সহ।",
    tileSIRaccent: "ডেটা এক্সপ্লোরার",
    tileBoothtitle: "বুথ–গ্রাম পঞ্চায়েত/পৌরসভা মানচিত্রায়ন",
    tileBoothsub: "বুথ-ভিত্তিক পঞ্চায়েত / পৌরসভা বণ্টন",
    tileBoothdesc: "প্রতিটি বুথকে তার গ্রাম পঞ্চায়েত বা পৌর ওয়ার্ডে ম্যাপ করুন।",
    tileBoothaccent: "সংগঠন সরঞ্জাম",
    tileForm20title: "ফর্ম ২০ | পশ্চিমবঙ্গ ২০১১–২০২৬",
    tileForm20sub: "বুথ-স্তরের ফলাফল — ২০১১ · ২০১৪ · ২০১৬ · ২০১৯ · ২০২১ · ২০২৪ · ২০২৬",
    tileForm20desc: "২০১১ থেকে সমস্ত পশ্চিমবঙ্গ বিধানসভা ও লোকসভা নির্বাচনের বুথ-ভিত্তিক ভোট গণনা দেখুন।",
    tileForm20accent: "নির্বাচন ডেটা",
    tileDemographytitle: "বুথ জনবিন্যাস",
    tileDemographysub: "বুথ-ভিত্তিক জনতাত্ত্বিক বিভাজন",
    tileDemographydesc: "প্রতিটি বিধানসভা কেন্দ্রের বুথ-স্তরের জনতাত্ত্বিক গঠন দেখুন — লিঙ্গ, বয়স ও সম্প্রদায় অনুযায়ী।",
    tileDemographyaccent: "ডেটা এক্সপ্লোরার",
    tileMuniResultsTitle: "পুরসভা ফলাফল ও জনবিন্যাস",
    tileMuniResultssub: "২০২৬ বিধানসভা নির্বাচনের ওয়ার্ড-ভিত্তিক ফলাফল ও জনতাত্ত্বিক তথ্য",
    tileMuniResultsdesc: "প্রতিটি পুরসভার ওয়ার্ড অনুযায়ী ২০২৬ বিধানসভা ভোটের ফল ও জনবিন্যাস দেখুন।",
    tileMuniResultsaccent: "নির্বাচন + জনবিন্যাস",
    tileSurveyTitle: "পুরসভা সমীক্ষা",
    tileSurveysub: "বাড়ি-বাড়ি তথ্য সংগ্রহ · KMC · HMC · AMC",
    tileSurveydesc: "কলকাতা, হাওড়া ও আসানসোল পুরনিগমে ওয়ার্ড-ভিত্তিক গণসংযোগ ও সমীক্ষা।",
    tileSurveyaccent: "ক্ষেত্র সমীক্ষা · সর্বজনীন",
    tileSurveyAdmin: "সমীক্ষা অ্যাডমিন",
    tileMediaTitle: "মিডিয়া ওয়াচ",
    tileMediasub: "পশ্চিমবঙ্গের সংবাদ কভারেজের দৈনিক পর্যবেক্ষণ",
    tileMediadesc: "রাজ্য ও জাতীয় সংবাদমাধ্যম কীভাবে পশ্চিমবঙ্গকে দেখাচ্ছে — শিরোনাম, উপস্থাপন ও বাছাই।",
    tileMediaaccent: "সংবাদ পর্যবেক্ষণ",
    tileBaithakTitle: "পাড়া বৈঠক",
    tileBaithaksub: "স্থানীয় সভার বিবরণ, উপস্থিতি ও কার্যবিবরণী রেকর্ড করুন",
    tileBaithakdesc: "রাজ্যজুড়ে প্রতিটি পাড়া বৈঠক লিপিবদ্ধ করুন — উপস্থিতি, কার্যবিবরণী ও লোকেশন-ট্যাগ করা ছবি সহ।",
    tileBaithakaccent: "ফিল্ড রিপোর্টিং",
    authorisedOnly: "শুধুমাত্র অনুমোদিত সংগঠকদের জন্য",
  },
};
const LangCtx = createContext();
const useT = () => useContext(LangCtx);

// --- Auth ---
const AuthCtx = createContext();
const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUser(r.data))
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setLoading(false));
  }, []);
  const login = async (email, password, module = "lid") => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password, module });
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  };
  const logout = () => { localStorage.removeItem("token"); setUser(null); };
  return <AuthCtx.Provider value={{ user, loading, login, logout }}>{children}</AuthCtx.Provider>;
}

axios.interceptors.request.use(cfg => {
  const t = localStorage.getItem("token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x.msg).join(", ");
  return e.message || "Error";
};

// --- Public Landing Portal ---
function LandingPortal() {
  const { t, lang, setLang } = useT();
  const nav = useNavigate();
  const tiles = [
    {
      title: t("tileMediaTitle"),
      subtitle: t("tileMediasub"),
      description: t("tileMediadesc"),
      cta: t("open"),
      testid: "tile-media",
      onClick: () => { window.location.href = "/pages/media/index.html"; },
      accent: t("tileMediaaccent"),
    },
    {
      title: t("tileSurveyTitle"),
      subtitle: t("tileSurveysub"),
      description: t("tileSurveydesc"),
      cta: t("open"),
      testid: "tile-surveys",
      onClick: () => { window.location.href = "/pages/surveys/index.html"; },
      accent: t("tileSurveyaccent"),
    },
    {
      title: t("tileMuniResultsTitle"),
      subtitle: t("tileMuniResultssub"),
      description: t("tileMuniResultsdesc"),
      cta: t("open"),
      testid: "tile-muni-results",
      onClick: () => { window.location.href = "/pages/municipality/index.html"; },
      accent: t("tileMuniResultsaccent"),
    },
    {
      title: t("tileBaithakTitle"),
      subtitle: t("tileBaithaksub"),
      description: t("tileBaithakdesc"),
      cta: t("enter"),
      testid: "tile-baithak",
      onClick: () => nav("/baithaks"),
      accent: t("tileBaithakaccent"),
    },
    {
      title: t("tileForm20title"),
      subtitle: t("tileForm20sub"),
      description: t("tileForm20desc"),
      cta: t("open"),
      testid: "tile-form20",
      onClick: () => { window.location.href = "/pages/elections/index.html"; },
      accent: t("tileForm20accent"),
    },
    {
      title: t("tileDemographytitle"),
      subtitle: t("tileDemographysub"),
      description: t("tileDemographydesc"),
      cta: t("open"),
      testid: "tile-demography",
      onClick: () => { window.location.href = "/pages/demography.html"; },
      accent: t("tileDemographyaccent"),
    },
    {
      title: t("tileBoothtitle"),
      subtitle: t("tileBoothsub"),
      description: t("tileBoothdesc"),
      cta: t("open"),
      testid: "tile-booth",
      onClick: () => { window.location.href = "/pages/booth.html"; },
      accent: t("tileBoothaccent"),
    },
    {
      title: t("tileSIRtitle"),
      subtitle: t("tileSIRsub"),
      description: t("tileSIRdesc"),
      cta: t("open"),
      testid: "tile-sir",
      onClick: () => { window.location.href = "/pages/sir.html"; },
      accent: t("tileSIRaccent"),
    },
    {
      title: t("tileLIDtitle"),
      subtitle: t("tileLIDsub"),
      description: t("tileLIDdesc"),
      cta: t("enter"),
      testid: "tile-lid",
      onClick: () => nav("/dashboard"),
      accent: t("tileLIDaccent"),
    },
  ];
  return (
    <div className={`min-h-screen bg-background ${lang==="bn"?"font-bn":""}`}>
      <header className="border-b-2 border-primary bg-card">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 md:px-8 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white flex items-center justify-center border-2 border-accent">
              <CPIMLogo className="w-9 h-9"/>
            </div>
            <div>
              <div className="font-black text-lg md:text-xl tracking-tight">LeftTarget</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">CPI(M) West Bengal</div>
            </div>
          </div>
          <div className="flex gap-1 text-xs">
            <button data-testid="lang-en" onClick={()=>setLang("en")} className={`px-2 py-1 border ${lang==="en"?"bg-primary text-primary-foreground border-primary":"border-border"}`}>EN</button>
            <button data-testid="lang-bn" onClick={()=>setLang("bn")} className={`px-2 py-1 border ${lang==="bn"?"bg-primary text-primary-foreground border-primary":"border-border"}`}>বাংলা</button>
          </div>
        </div>
      </header>

      <section className="bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 15% 30%, rgba(212,175,55,0.35), transparent 55%), radial-gradient(circle at 85% 70%, rgba(255,255,255,0.15), transparent 55%)'}}/>
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-24 relative z-10">
          <h1 className={`text-4xl md:text-6xl font-black tracking-tight max-w-3xl ${lang==="bn"?"leading-[1.35]":"leading-tight"}`}>
            {t("portalTitle")}<br/>
            <span className="text-accent">{t("portalTitle2")}</span>
          </h1>
          <p className="mt-6 text-base md:text-lg opacity-85 max-w-2xl">
            {t("portalSub")}
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-6 flex items-center gap-2">
          <div className="w-6 h-px bg-primary"/> {t("chooseTool")}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {tiles.map(tile => (
            <button
              key={tile.testid}
              data-testid={tile.testid}
              onClick={tile.onClick}
              className="group text-left border border-border bg-card hover:border-primary transition-colors relative overflow-hidden"
            >
              <div className="p-6 md:p-8">
                <div className="text-[10px] uppercase tracking-[0.25em] text-accent mb-4">{tile.accent}</div>
                <h3 className="text-2xl md:text-3xl font-black tracking-tight leading-tight">{tile.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground font-semibold">{tile.subtitle}</p>
                <p className="mt-4 text-sm text-foreground/80 leading-relaxed">{tile.description}</p>
                <div className="mt-8">
                  <span className="text-sm font-bold uppercase tracking-widest text-primary group-hover:text-accent transition-colors inline-flex items-center gap-2">
                    {tile.cta} <ChevronRight className="w-4 h-4"/>
                  </span>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary scale-x-0 group-hover:scale-x-100 origin-left transition-transform"/>
            </button>
          ))}
        </div>
      </section>

      <footer className="border-t border-border py-6 mt-10">
        <div className="max-w-6xl mx-auto px-4 md:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs uppercase tracking-widest text-muted-foreground">
          <div>© CPI(M) West Bengal · LeftTarget</div>
          <div>{t("authorisedOnly")}</div>
        </div>
      </footer>
    </div>
  );
}

// --- Login Page ---
function LoginPage() {
  const { login, user } = useAuth();
  const { t, lang, setLang } = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const nextPath = loc.state?.from || new URLSearchParams(loc.search).get("next") || "/dashboard";
  useEffect(() => { if (user) nav(nextPath, { replace: true }); }, [user, nav, nextPath]);
  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await login(email, password, "lid"); toast.success("Welcome"); nav(nextPath, { replace: true }); }
    catch (er) { toast.error(errMsg(er)); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 30%, rgba(212,175,55,0.3), transparent 50%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.15), transparent 50%)'}}/>
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 border-2 border-accent flex items-center justify-center bg-white" data-testid="cpim-logo">
              <CPIMLogo className="w-10 h-10"/>
            </div>
            <span className="tracking-widest text-xs uppercase text-accent">Restricted · Party Internal</span>
          </div>
          <h1 className="mt-16 text-5xl lg:text-6xl font-black leading-tight tracking-tight">Local Influencer Database</h1>
        </div>
        <div className="relative z-10 text-xs uppercase tracking-widest opacity-70">© CPI(M) West Bengal</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="flex justify-between items-center">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Local Influencer Database · Sign In</span>
            <div className="flex gap-1 text-xs">
              <button type="button" data-testid="lang-en" onClick={()=>setLang("en")} className={`px-2 py-1 border ${lang==="en"?"bg-primary text-primary-foreground border-primary":"border-border"}`}>EN</button>
              <button type="button" data-testid="lang-bn" onClick={()=>setLang("bn")} className={`px-2 py-1 border ${lang==="bn"?"bg-primary text-primary-foreground border-primary":"border-border"}`}>বাং</button>
            </div>
          </div>
          <div>
            <h2 className="text-3xl font-bold tracking-tight">{t("loginTitle")}</h2>
            <p className="text-sm text-muted-foreground mt-2">Authorised administrators only.</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label>{t("email")}</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required/>
            </div>
            <div>
              <Label>{t("password")}</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={e=>setPassword(e.target.value)} required/>
            </div>
          </div>
          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11">{loading ? "..." : t("login")}</Button>
          <div className="text-center">
            <Link data-testid="forgot-pw-link" to="/forgot-password" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary underline underline-offset-4">
              {t("forgotPassword") || "Forgot Password?"}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Layout ---
function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useT();
  const loc = useLocation();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);
  const isSurveyAdmin = loc.pathname.startsWith("/surveys/");
  const headerTitle = isSurveyAdmin ? (t("tileSurveyTitle") || "Municipal Surveys") : t("app");
  const headerSubtitle = isSurveyAdmin ? (t("tileSurveyAdmin") || "Survey Admin") : "CPI(M) West Bengal";
  const items = [
    { to: "/dashboard", icon: LayoutDashboard, label: t("dashboard") },
    { to: "/influencers", icon: Users, label: t("influencers") },
    { to: "/events", icon: Calendar, label: t("events") },
    { to: "/categories", icon: Tag, label: t("categories") },
  ];
  if (user?.role !== "district_admin") {
    items.push({ to: "/audit", icon: Activity, label: t("audit") });
  }
  if (user?.role === "super_admin" || user?.role === "state_admin") {
    items.push({ to: "/surveys/admin", icon: ClipboardList, label: t("tileSurveyAdmin") || "Survey Admin" });
  }
  if (user?.role === "super_admin") {
    items.push({ to: "/users", icon: Shield, label: t("users") });
  }

  const sidebarInner = (
    <>
      <div className="p-4 md:p-6 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-white flex items-center justify-center border border-accent"><CPIMLogo className="w-7 h-7"/></div>
          <div>
            <div className="font-black tracking-tight text-sm leading-tight">{headerTitle}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{headerSubtitle}</div>
          </div>
        </div>
        <button className="lg:hidden text-muted-foreground p-1" onClick={()=>setMobileOpen(false)} data-testid="close-mobile-nav" aria-label="Close menu"><X className="w-5 h-5"/></button>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {items.map(it => {
          const active = loc.pathname === it.to || (it.to !== "/dashboard" && loc.pathname.startsWith(it.to));
          const Icon = it.icon;
          return (
            <Link key={it.to} to={it.to} data-testid={`nav-${it.to.replace(/\//g,"") || "dashboard"}`}
              className={`flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 ${active ? "bg-secondary text-foreground border-accent font-semibold" : "text-muted-foreground border-transparent hover:text-foreground hover:bg-secondary/50"}`}>
              <Icon className="w-4 h-4"/> {it.label}
            </Link>
          );
        })}
      </nav>
        <div className="p-4 border-t border-border">
        <Link to="/" data-testid="back-to-portal" className="flex items-center justify-center gap-1 mb-2 border border-primary text-primary hover:bg-secondary text-xs uppercase tracking-widest px-2 py-2 font-semibold">← {t("backToPortal") || "Back to Portal"}</Link>
        <Button data-testid="change-pw-btn" variant="outline" size="sm" className="w-full mb-2" onClick={()=>setChangePwOpen(true)}><Key className="w-3 h-3 mr-1"/>{t("changePassword") || "Change Password"}</Button>
          <div className="flex gap-1 mb-3 text-xs">
          <button data-testid="lang-en" onClick={()=>setLang("en")} className={`flex-1 px-2 py-1 border ${lang==="en"?"bg-primary text-primary-foreground border-primary":"border-border"}`}>EN</button>
          <button data-testid="lang-bn" onClick={()=>setLang("bn")} className={`flex-1 px-2 py-1 border ${lang==="bn"?"bg-primary text-primary-foreground border-primary":"border-border"}`}>বাংলা</button>
        </div>
        <div className="text-xs">
          <div className="font-semibold truncate">{user?.name}</div>
          <div className="text-muted-foreground uppercase tracking-widest text-[10px]">{user?.role?.replace("_"," ")}</div>
          {user?.district && <div className="text-muted-foreground text-[10px]">{user.district}</div>}
        </div>
        <Button data-testid="logout-btn" variant="outline" size="sm" className="w-full mt-3" onClick={()=>{logout(); nav("/login");}}><LogOut className="w-3 h-3 mr-1"/>{t("logout")}</Button>
      </div>
    </>
  );

  return (
    <div className={`min-h-screen bg-background lg:flex ${lang==="bn"?"font-bn":""}`}>
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 bg-card border-b border-border flex items-center justify-between px-4 py-3">
        <button onClick={()=>setMobileOpen(true)} className="p-1" data-testid="open-mobile-nav" aria-label="Open menu"><Menu className="w-6 h-6"/></button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-white flex items-center justify-center border border-accent"><CPIMLogo className="w-5 h-5"/></div>
          <div className="font-black text-xs tracking-tight">{headerTitle}</div>
        </div>
        <div className="w-8"/>
      </div>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={()=>setMobileOpen(false)}/>
      )}

      {/* Sidebar — fixed drawer on mobile, static on desktop */}
      <aside className={`fixed lg:static top-0 left-0 z-50 h-full w-72 lg:w-64 border-r border-border bg-card flex flex-col transition-transform lg:transition-none ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        {sidebarInner}
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">{children}</main>
      <ChangePasswordDialog open={changePwOpen} onClose={()=>setChangePwOpen(false)}/>
    </div>
  );
}

function ChangePasswordDialog({ open, onClose }) {
  const { t } = useT();
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [conf, setConf] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (np.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (np !== conf) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: cur, new_password: np });
      toast.success("Password changed");
      setCur(""); setNp(""); setConf("");
      onClose();
    } catch(er) { toast.error(errMsg(er)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o)=>{ if(!o){ setCur(""); setNp(""); setConf(""); onClose(); }}}>
      <DialogContent data-testid="change-pw-dialog">
        <DialogHeader><DialogTitle>{t("changePassword") || "Change Password"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>{t("currentPassword") || "Current Password"}</Label><Input data-testid="cp-current" type="password" required value={cur} onChange={e=>setCur(e.target.value)}/></div>
          <div><Label>{t("newPassword") || "New Password"}</Label><Input data-testid="cp-new" type="password" required value={np} onChange={e=>setNp(e.target.value)}/></div>
          <div><Label>{t("confirmPassword") || "Confirm Password"}</Label><Input data-testid="cp-confirm" type="password" required value={conf} onChange={e=>setConf(e.target.value)}/></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t("cancel")}</Button>
            <Button data-testid="cp-submit" type="submit" disabled={busy}>{busy?"...":t("save")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 border-b border-border px-4 md:px-8 py-4 md:py-6 bg-card">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{subtitle}</div>
        <h1 className="text-2xl md:text-3xl font-black tracking-tight mt-1">{title}</h1>
      </div>
      {action && <div className="flex flex-wrap gap-2">{action}</div>}
    </div>
  );
}

// --- Dashboard ---
function Dashboard() {
  const { t } = useT();
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/reports/summary`).then(r=>setData(r.data)).catch(e=>toast.error(errMsg(e))); }, []);
  if (!data) return <div className="p-8">Loading...</div>;
  const colors = ["#991B1B","#B8860B","#7F1D1D","#DC2626","#D4AF37","#F87171"];
  return (
    <div>
      <PageHeader title={t("dashboard")} subtitle="Overview"/>
      <div className="p-4 md:p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {[
            {label:t("totalInfluencers"), val:data.totals.influencers, tid:"kpi-influencers"},
            {label:t("totalEvents"), val:data.totals.events, tid:"kpi-events"},
            {label:t("participations"), val:data.totals.participations, tid:"kpi-participations"},
            {label:t("unengaged"), val:data.totals.unengaged, tid:"kpi-unengaged"},
          ].map(k=>(
            <div key={k.label} data-testid={k.tid} className="border border-border bg-card p-4 md:p-6">
              <div className="text-[10px] md:text-xs uppercase tracking-widest text-muted-foreground">{k.label}</div>
              <div className="text-3xl md:text-4xl font-black mt-2 tracking-tight">{k.val}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader><CardTitle className="text-sm uppercase tracking-widest">{t("byCategory")}</CardTitle></CardHeader>
            <CardContent style={{height: 260}}>
              {data.by_category.length === 0 ? <div className="text-sm text-muted-foreground">{t("noResults")}</div> :
              <ResponsiveContainer><PieChart><Pie data={data.by_category} dataKey="count" nameKey="name" outerRadius={80}>
                {data.by_category.map((_,i)=><Cell key={i} fill={colors[i%colors.length]}/>)}
              </Pie><Legend/><Tooltip/></PieChart></ResponsiveContainer>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-sm uppercase tracking-widest">{t("byAC")}</CardTitle></CardHeader>
            <CardContent style={{height: 260}}>
              {data.by_ac.length === 0 ? <div className="text-sm text-muted-foreground">{t("noResults")}</div> :
              <ResponsiveContainer><BarChart data={data.by_ac}><CartesianGrid strokeDasharray="3 3" opacity={0.2}/><XAxis dataKey="name" tick={{fontSize:10}}/><YAxis tick={{fontSize:10}}/><Tooltip/><Bar dataKey="count" fill="#991B1B"/></BarChart></ResponsiveContainer>}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader><CardTitle className="text-sm uppercase tracking-widest">{t("eventsByType")}</CardTitle></CardHeader>
            <CardContent style={{height:220}}>
              {data.events_by_type.length===0 ? <div className="text-sm text-muted-foreground">{t("noResults")}</div> :
              <ResponsiveContainer><BarChart data={data.events_by_type}><CartesianGrid strokeDasharray="3 3" opacity={0.2}/><XAxis dataKey="name" tick={{fontSize:10}}/><YAxis tick={{fontSize:10}}/><Tooltip/><Bar dataKey="count" fill="#B8860B"/></BarChart></ResponsiveContainer>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-sm uppercase tracking-widest">{t("recentEvents")}</CardTitle></CardHeader>
            <CardContent>
              {data.recent_events.length===0 ? <div className="text-sm text-muted-foreground">{t("noResults")}</div> :
              <div className="space-y-2">{data.recent_events.map(e=>(
                <Link key={e.id} to={`/events/${e.id}`} className="flex items-center justify-between py-2 border-b border-border hover:bg-secondary/40 px-2">
                  <div><div className="font-semibold text-sm">{e.event_name}</div><div className="text-xs text-muted-foreground">{e.event_date} · {e.district}</div></div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground"/>
                </Link>
              ))}</div>}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// --- Helpers for geography selects ---
function useGeo() {
  const [districts, setDistricts] = useState([]);
  const [acs, setAcs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/geo/districts`).then(r=>setDistricts(r.data));
    axios.get(`${API}/geo/acs`).then(r=>setAcs(r.data));
  }, []);
  return { districts, acs };
}

// Skill Area (Influence Level) options
const SKILL_AREAS = [
  { value: "State", label: "State" },
  { value: "Local Assembly", label: "Local Assembly" },
  { value: "Local Municipality", label: "Local Municipality" },
  { value: "Local Gram Panchayat", label: "Local Gram Panchayat" },
];

// Cascading Locality Fields: District → AC → Municipality → Ward + Panchayat → Village
function LocalityFields({ form, setForm, districts, acs, testPrefix = "" }) {
  const { t } = useT();
  const [munis, setMunis] = useState([]);
  const [panchs, setPanchs] = useState([]);
  const acsFiltered = form.district ? acs.filter(a => a.district === form.district) : acs;

  useEffect(() => {
    if (form.ac_no == null) { setMunis([]); setPanchs([]); return; }
    axios.get(`${API}/geo/municipalities`, { params: { ac_no: form.ac_no } }).then(r => setMunis(r.data));
    axios.get(`${API}/geo/panchayats`, { params: { ac_no: form.ac_no } }).then(r => setPanchs(r.data));
  }, [form.ac_no]);

  const selectedMun = munis.find(m => m.municipality === form.municipality);
  const selectedPan = panchs.find(p => p.panchayat === form.panchayat);

  return (
    <Section title={t("locality")}>
      <Field label={t("district")}>
        <Select value={form.district || "none"} onValueChange={v => setForm({ ...form, district: v==="none"?"":v, ac_no: null, ac_name: "", municipality: "", ward: "", panchayat: "", village: "" })}>
          <SelectTrigger data-testid={`${testPrefix}district`}><SelectValue placeholder="—"/></SelectTrigger>
          <SelectContent><SelectItem value="none">—</SelectItem>{districts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label={t("ac")}>
        <Select value={form.ac_no ? String(form.ac_no) : "none"} onValueChange={v => {
          if (v === "none") { setForm({ ...form, ac_no: null, ac_name: "", municipality: "", ward: "", panchayat: "", village: "" }); return; }
          const ac = acsFiltered.find(a => String(a.ac_no) === v);
          setForm({ ...form, ac_no: ac?.ac_no, ac_name: ac?.ac_name || "", municipality: "", ward: "", panchayat: "", village: "" });
        }}>
          <SelectTrigger data-testid={`${testPrefix}ac`}><SelectValue placeholder="—"/></SelectTrigger>
          <SelectContent><SelectItem value="none">—</SelectItem>{acsFiltered.map(a => <SelectItem key={a.ac_no} value={String(a.ac_no)}>{a.ac_name}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label={t("municipality")}>
        <Select value={form.municipality || "none"} onValueChange={v => setForm({ ...form, municipality: v==="none"?"":v, ward: "" })} disabled={!form.ac_no}>
          <SelectTrigger data-testid={`${testPrefix}municipality`}><SelectValue placeholder={form.ac_no ? "—" : "Select AC first"}/></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">—</SelectItem>
            {munis.map(m => <SelectItem key={m.id} value={m.municipality}>{m.municipality}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label={t("ward")}>
        <Select value={form.ward || "none"} onValueChange={v => setForm({ ...form, ward: v==="none"?"":v })} disabled={!selectedMun}>
          <SelectTrigger data-testid={`${testPrefix}ward`}><SelectValue placeholder={selectedMun ? "—" : "Select Municipality"}/></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">—</SelectItem>
            {(selectedMun?.wards || []).map(w => <SelectItem key={w} value={w}>Ward {w}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label={t("panchayat")}>
        <Select value={form.panchayat || "none"} onValueChange={v => setForm({ ...form, panchayat: v==="none"?"":v, village: "" })} disabled={!form.ac_no}>
          <SelectTrigger data-testid={`${testPrefix}panchayat`}><SelectValue placeholder={form.ac_no ? "—" : "Select AC first"}/></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">—</SelectItem>
            {panchs.map(p => <SelectItem key={p.id} value={p.panchayat}>{p.panchayat}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label={t("village")}>
        <Select value={form.village || "none"} onValueChange={v => setForm({ ...form, village: v==="none"?"":v })} disabled={!selectedPan}>
          <SelectTrigger data-testid={`${testPrefix}village`}><SelectValue placeholder={selectedPan ? "—" : "Select Panchayat"}/></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">—</SelectItem>
            {(selectedPan?.villages || []).map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
    </Section>
  );
}

// Trigger CSV download (Authorization header required)
async function downloadCSV(path, filename, params = {}) {
  try {
    const r = await axios.get(`${API}${path}`, { params, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    toast.success("Export downloaded");
  } catch (e) { toast.error(errMsg(e)); }
}

// --- Influencers ---
function InfluencersPage() {
  const { t } = useT();
  const [data, setData] = useState({total:0, items:[]});
  const [q, setQ] = useState("");
  const [district, setDistrict] = useState("all");
  const [categoryId, setCategoryId] = useState("all");
  const [cats, setCats] = useState([]);
  const { districts } = useGeo();
  const nav = useNavigate();
  const load = useCallback(() => {
    const p = {};
    if (q) p.q = q;
    if (district !== "all") p.district = district;
    if (categoryId !== "all") p.category_id = categoryId;
    axios.get(`${API}/influencers`, { params: p }).then(r=>setData(r.data));
  }, [q, district, categoryId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/categories`).then(r=>setCats(r.data.filter(c=>!c.parent_id))); }, []);
  const { user } = useAuth();
  const canManage = user?.role === "super_admin" || user?.role === "state_admin";
  const onDelete = async (e, id, name) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await axios.delete(`${API}/influencers/${id}`); toast.success("Deleted"); load(); }
    catch(er) { toast.error(errMsg(er)); }
  };
  return (
    <div>
      <PageHeader title={t("influencers")} subtitle={`${data.total} records`}
        action={<div className="flex gap-2">
          <Button data-testid="export-influencers-btn" variant="outline" onClick={()=>downloadCSV("/export/influencers.csv", "influencers.csv", { ...(q?{q}:{}) , ...(district!=="all"?{district}:{}), ...(categoryId!=="all"?{category_id:categoryId}:{}) })}><Download className="w-4 h-4 mr-1"/>{t("export")}</Button>
          <Button data-testid="new-influencer-btn" onClick={()=>nav("/influencers/new")}><Plus className="w-4 h-4 mr-1"/>{t("createInfluencer")}</Button>
        </div>}/>
      <div className="p-4 md:p-8 flex flex-col lg:flex-row gap-6">
        <aside className="w-full lg:w-64 shrink-0 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-1 gap-3 lg:space-y-4 lg:block">
          <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1 md:col-span-3 lg:col-span-1"><Filter className="w-3 h-3"/>{t("filter")}</div>
          <div><Label>{t("search")}</Label><Input data-testid="influencer-search" value={q} onChange={e=>setQ(e.target.value)}/></div>
          <div><Label>{t("district")}</Label>
            <Select value={district} onValueChange={setDistrict}><SelectTrigger data-testid="filter-district"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="all">{t("allDistricts")}</SelectItem>{districts.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>{t("category")}</Label>
            <Select value={categoryId} onValueChange={setCategoryId}><SelectTrigger data-testid="filter-category"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="all">{t("allCategories")}</SelectItem>{cats.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select></div>
        </aside>
        <div className="flex-1 min-w-0 border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t("name")}</TableHead><TableHead>{t("contact")}</TableHead><TableHead>{t("district")}</TableHead>
              <TableHead>{t("ac")}</TableHead><TableHead>{t("profession")}</TableHead><TableHead>{t("status")}</TableHead>
              <TableHead>{t("action")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">{t("noResults")}</TableCell></TableRow>}
              {data.items.map(i=>(
                <TableRow key={i.id} data-testid={`influencer-row-${i.id}`} className="cursor-pointer" onClick={()=>nav(`/influencers/${i.id}`)}>
                  <TableCell className="font-semibold">{i.name}</TableCell><TableCell>{i.contact_number}</TableCell>
                  <TableCell>{i.district}</TableCell><TableCell>{i.ac_name}</TableCell>
                  <TableCell>{i.profession}</TableCell>
                  <TableCell><Badge variant="outline">{i.engagement_status}</Badge></TableCell>
                  <TableCell onClick={e=>e.stopPropagation()}>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" data-testid={`edit-inf-${i.id}`} onClick={()=>nav(`/influencers/${i.id}`)}><Edit className="w-3 h-3"/></Button>
                      {canManage && <Button size="sm" variant="outline" className="text-destructive" data-testid={`del-inf-${i.id}`} onClick={(e)=>onDelete(e, i.id, i.name)}><Trash2 className="w-3 h-3"/></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function InfluencerForm() {
  const { t } = useT();
  const { id } = useParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const { user } = useAuth();
  const { districts, acs } = useGeo();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({name:"", contact_number:"", email:"", age_group:"", gender:"", district: user?.district || "", ac_no:null, ac_name:"", municipality:"", panchayat:"", ward:"", village:"", profession:"", category_id:"", subcategory_id:"", skill_area:"", profile_brief:"", engagement_status:"active", photo_base64:""});
  const [details, setDetails] = useState(null);
  useEffect(() => { axios.get(`${API}/categories`).then(r=>setCats(r.data)); }, []);
  useEffect(() => {
    if (!isNew) axios.get(`${API}/influencers/${id}`).then(r=>{ setForm(r.data); setDetails(r.data); });
  }, [id, isNew]);
  const rootCats = cats.filter(c=>!c.parent_id);
  const subcats = cats.filter(c=>c.parent_id === form.category_id);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2000000) { toast.error("Photo too large (>2MB)"); return; }
    const r = new FileReader();
    r.onload = () => setForm(p=>({...p, photo_base64: r.result}));
    r.readAsDataURL(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isNew) { await axios.post(`${API}/influencers`, form); toast.success("Created"); }
      else { await axios.patch(`${API}/influencers/${id}`, form); toast.success("Updated"); }
      nav("/influencers");
    } catch (er) { toast.error(errMsg(er)); }
  };

  return (
    <div>
      <PageHeader title={isNew ? t("createInfluencer") : form.name} subtitle={t("influencers")}/>
      <form onSubmit={onSubmit} className="p-4 md:p-8 max-w-5xl space-y-6 md:space-y-8" data-testid="influencer-form">
        <Section title={t("basicInfo")}>
          <Field label={t("name")}><Input data-testid="inf-name" required value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></Field>
          <Field label={t("contact")}><Input data-testid="inf-contact" required value={form.contact_number} onChange={e=>setForm({...form, contact_number:e.target.value})}/></Field>
          <Field label={t("email")}><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/></Field>
          <Field label={t("ageGroup")}>
            <Select value={form.age_group || "none"} onValueChange={v=>setForm({...form, age_group: v==="none"?"":v})}><SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{["18-25","26-35","36-45","46-60","60+"].map(a=><SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label={t("gender")}>
            <Select value={form.gender || "none"} onValueChange={v=>setForm({...form, gender: v==="none"?"":v})}><SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent></Select>
          </Field>
          <Field label={t("profession")}><Input data-testid="inf-profession" required value={form.profession} onChange={e=>setForm({...form, profession:e.target.value})}/></Field>
        </Section>

        <LocalityFields form={form} setForm={setForm} districts={districts} acs={acs} testPrefix="inf-"/>

        <Section title={t("influence")}>
          <Field label={t("category")}>
            <Select value={form.category_id || "none"} onValueChange={v=>setForm({...form, category_id: v==="none"?"":v, subcategory_id:""})}><SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{rootCats.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label={t("subcategory")}>
            <Select value={form.subcategory_id || "none"} onValueChange={v=>setForm({...form, subcategory_id: v==="none"?"":v})}><SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{subcats.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label={t("skillArea")}>
            <Select value={form.skill_area || "none"} onValueChange={v=>setForm({...form, skill_area: v==="none"?"":v})}>
              <SelectTrigger data-testid="inf-skill-area"><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{SKILL_AREAS.map(s=><SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("engagementStatus")}>
            <Select value={form.engagement_status} onValueChange={v=>setForm({...form, engagement_status:v})}><SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{["active","inactive","potential"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label={t("profileBrief")} full><Textarea rows={3} value={form.profile_brief} onChange={e=>setForm({...form, profile_brief:e.target.value})}/></Field>
          <Field label={t("photo")}>
            <input type="file" accept="image/*" onChange={onPhoto} data-testid="inf-photo" className="text-xs"/>
            {form.photo_base64 && <img src={form.photo_base64} alt="preview" className="mt-2 w-24 h-24 object-cover border border-border"/>}
          </Field>
        </Section>

        <div className="flex gap-2">
          <Button data-testid="inf-save" type="submit">{t("save")}</Button>
          <Button type="button" variant="outline" onClick={()=>nav("/influencers")}>{t("cancel")}</Button>
        </div>

        {details?.participations?.length > 0 && (
          <div>
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Event History</h3>
            <div className="border border-border bg-card">
              <Table><TableHeader><TableRow><TableHead>Event</TableHead><TableHead>Date</TableHead><TableHead>{t("invitationStatus")}</TableHead></TableRow></TableHeader>
                <TableBody>{details.participations.map(p=>(
                  <TableRow key={p.id}><TableCell><Link to={`/events/${p.event_id}`} className="underline">{p.event?.event_name}</Link></TableCell><TableCell>{p.event?.event_date}</TableCell><TableCell><Badge>{p.invitation_status}</Badge></TableCell></TableRow>
                ))}</TableBody></Table>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="border border-border bg-card p-6">
    <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  </section>
);
const Field = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}><Label className="text-xs">{label}</Label>{children}</div>
);

function InvitationsPanel({ invitations, eventName }) {
  const { t } = useT();
  const [enMsg, setEnMsg] = useState(invitations.english || "");
  const [bnMsg, setBnMsg] = useState(invitations.bengali || "");
  useEffect(() => { setEnMsg(invitations.english || ""); setBnMsg(invitations.bengali || ""); }, [invitations.english, invitations.bengali]);
  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success(t("copied")); }
    catch { toast.error("Copy failed"); }
  };
  const openWA = (text) => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  return (
    <section className="border border-border bg-card p-6" data-testid="invitations-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2"><MessageCircle className="w-3.5 h-3.5"/>{t("invitations")}</h3>
        <span className="text-[10px] text-muted-foreground">{eventName}</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-widest">{t("englishMsg")}</Label>
            <div className="flex gap-1">
              <button type="button" data-testid="copy-en-btn" onClick={()=>copyToClipboard(enMsg)} className="px-2 py-1 text-xs border border-border hover:bg-secondary inline-flex items-center gap-1"><Copy className="w-3 h-3"/>{t("copy")}</button>
              <button type="button" data-testid="wa-en-share-btn" onClick={()=>openWA(enMsg)} className="px-2 py-1 text-xs bg-primary text-primary-foreground hover:opacity-90 inline-flex items-center gap-1"><Send className="w-3 h-3"/>{t("openWhatsApp")}</button>
            </div>
          </div>
          <Textarea data-testid="invitation-en" rows={10} value={enMsg} onChange={e=>setEnMsg(e.target.value)} className="font-mono text-xs"/>
        </div>
        <div className="space-y-2 font-bn">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-widest">{t("bengaliMsg")}</Label>
            <div className="flex gap-1">
              <button type="button" data-testid="copy-bn-btn" onClick={()=>copyToClipboard(bnMsg)} className="px-2 py-1 text-xs border border-border hover:bg-secondary inline-flex items-center gap-1"><Copy className="w-3 h-3"/>{t("copy")}</button>
              <button type="button" data-testid="wa-bn-share-btn" onClick={()=>openWA(bnMsg)} className="px-2 py-1 text-xs bg-accent text-accent-foreground hover:opacity-90 inline-flex items-center gap-1"><Send className="w-3 h-3"/>{t("openWhatsApp")}</button>
            </div>
          </div>
          <Textarea data-testid="invitation-bn" rows={10} value={bnMsg} onChange={e=>setBnMsg(e.target.value)} className="text-xs"/>
        </div>
      </div>
    </section>
  );
}

// --- Events ---
function EventsPage() {
  const { t } = useT();
  const [data, setData] = useState({total:0, items:[]});
  const [q, setQ] = useState("");
  const [district, setDistrict] = useState("all");
  const { districts } = useGeo();
  const nav = useNavigate();
  const load = useCallback(() => {
    const p = {};
    if (q) p.q = q;
    if (district !== "all") p.district = district;
    axios.get(`${API}/events`, { params: p }).then(r=>setData(r.data));
  }, [q, district]);
  useEffect(() => { load(); }, [load]);
  const { user } = useAuth();
  const canManage = user?.role === "super_admin" || user?.role === "state_admin";
  const onDelete = async (e, id, name) => {
    e.stopPropagation();
    if (!window.confirm(`Delete event "${name}"?`)) return;
    try { await axios.delete(`${API}/events/${id}`); toast.success("Deleted"); load(); }
    catch(er) { toast.error(errMsg(er)); }
  };
  return (
    <div>
      <PageHeader title={t("events")} subtitle={`${data.total} records`}
        action={<div className="flex gap-2">
          <Button data-testid="export-events-btn" variant="outline" onClick={()=>downloadCSV("/export/events.csv", "events.csv", { ...(q?{q}:{}), ...(district!=="all"?{district}:{}) })}><Download className="w-4 h-4 mr-1"/>{t("export")}</Button>
          <Button data-testid="new-event-btn" onClick={()=>nav("/events/new")}><Plus className="w-4 h-4 mr-1"/>{t("createEvent")}</Button>
        </div>}/>
      <div className="p-4 md:p-8 flex flex-col lg:flex-row gap-6">
        <aside className="w-full lg:w-64 shrink-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-3 lg:space-y-4 lg:block">
          <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1 md:col-span-2 lg:col-span-1"><Filter className="w-3 h-3"/>{t("filter")}</div>
          <div><Label>{t("search")}</Label><Input data-testid="event-search" value={q} onChange={e=>setQ(e.target.value)}/></div>
          <div><Label>{t("district")}</Label>
            <Select value={district} onValueChange={setDistrict}><SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="all">{t("allDistricts")}</SelectItem>{districts.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
        </aside>
        <div className="flex-1 min-w-0 border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>{t("eventName")}</TableHead><TableHead>{t("eventType")}</TableHead><TableHead>{t("eventDate")}</TableHead><TableHead>{t("district")}</TableHead><TableHead>{t("venue")}</TableHead><TableHead>{t("action")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{t("noResults")}</TableCell></TableRow>}
              {data.items.map(e=>(
                <TableRow key={e.id} data-testid={`event-row-${e.id}`} className="cursor-pointer" onClick={()=>nav(`/events/${e.id}`)}>
                  <TableCell className="font-semibold">{e.event_name}</TableCell><TableCell>{e.event_type}</TableCell><TableCell>{e.event_date}</TableCell><TableCell>{e.district}</TableCell><TableCell>{e.venue}</TableCell>
                  <TableCell onClick={ev=>ev.stopPropagation()}>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" data-testid={`edit-ev-${e.id}`} onClick={()=>nav(`/events/${e.id}`)}><Edit className="w-3 h-3"/></Button>
                      {canManage && <Button size="sm" variant="outline" className="text-destructive" data-testid={`del-ev-${e.id}`} onClick={(ev)=>onDelete(ev, e.id, e.event_name)}><Trash2 className="w-3 h-3"/></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function EventForm() {
  const { t } = useT();
  const { id } = useParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const { user } = useAuth();
  const { districts, acs } = useGeo();
  const [form, setForm] = useState({event_name:"", event_type:"", event_date:"", description:"", district: user?.district || "", ac_no:null, ac_name:"", municipality:"", panchayat:"", ward:"", village:"", venue:"", organising_unit:"", estimated_attendance:0, outcome_remarks:"", follow_up_required:false, photos_base64:[]});
  const [details, setDetails] = useState(null);
  const [influencers, setInfluencers] = useState([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({influencer_id:"", invitation_status:"invited", nature_of_participation:"", level_of_involvement:"", response_feedback:""});

  const loadDetails = useCallback(() => {
    axios.get(`${API}/events/${id}`).then(r=>{ setForm({...r.data, event_date: r.data.event_date?.slice(0,10) || ""}); setDetails(r.data); });
  }, [id]);
  useEffect(() => { if (!isNew) loadDetails(); }, [isNew, loadDetails]);
  useEffect(() => { axios.get(`${API}/influencers`, {params:{limit:500}}).then(r=>setInfluencers(r.data.items)); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isNew) { const r = await axios.post(`${API}/events`, form); toast.success("Created"); nav(`/events/${r.data.id}`); }
      else { await axios.patch(`${API}/events/${id}`, form); toast.success("Updated"); loadDetails(); }
    } catch (er) { toast.error(errMsg(er)); }
  };

  const linkInfluencer = async () => {
    try {
      if (!linkForm.influencer_id) { toast.error("Select influencer"); return; }
      await axios.post(`${API}/participations`, { ...linkForm, event_id: id });
      toast.success("Linked");
      setLinkOpen(false);
      setLinkForm({influencer_id:"", invitation_status:"invited", nature_of_participation:"", level_of_involvement:"", response_feedback:""});
      loadDetails();
    } catch(er) { toast.error(errMsg(er)); }
  };

  return (
    <div>
      <PageHeader title={isNew ? t("createEvent") : form.event_name} subtitle={t("events")}
        action={!isNew && <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
          <DialogTrigger asChild><Button data-testid="link-influencer-btn"><Plus className="w-4 h-4 mr-1"/>{t("linkInfluencer")}</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>{t("linkInfluencer")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("influencers")}</Label>
                <Select value={linkForm.influencer_id} onValueChange={v=>setLinkForm({...linkForm, influencer_id:v})}>
                  <SelectTrigger data-testid="link-influencer-select"><SelectValue placeholder="Select"/></SelectTrigger>
                  <SelectContent>{influencers.map(i=><SelectItem key={i.id} value={i.id}>{i.name} · {i.district}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>{t("invitationStatus")}</Label>
                <Select value={linkForm.invitation_status} onValueChange={v=>setLinkForm({...linkForm, invitation_status:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{["invited","attended","declined","no_response"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>{t("nature")}</Label><Input value={linkForm.nature_of_participation} onChange={e=>setLinkForm({...linkForm, nature_of_participation:e.target.value})}/></div>
              <div><Label>{t("involvement")}</Label>
                <Select value={linkForm.level_of_involvement || "none"} onValueChange={v=>setLinkForm({...linkForm, level_of_involvement: v==="none"?"":v})}>
                  <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{["High","Medium","Low"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>{t("feedback")}</Label><Textarea value={linkForm.response_feedback} onChange={e=>setLinkForm({...linkForm, response_feedback:e.target.value})}/></div>
            </div>
            <DialogFooter><Button data-testid="link-save" onClick={linkInfluencer}>{t("save")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>}
      />
      <form onSubmit={onSubmit} className="p-4 md:p-8 max-w-5xl space-y-6 md:space-y-8" data-testid="event-form">
        <Section title={t("basicInfo")}>
          <Field label={t("eventName")}><Input data-testid="ev-name" required value={form.event_name} onChange={e=>setForm({...form, event_name:e.target.value})}/></Field>
          <Field label={t("eventType")}><Input data-testid="ev-type" required value={form.event_type} onChange={e=>setForm({...form, event_type:e.target.value})}/></Field>
          <Field label={t("eventDate")}><Input type="date" required value={form.event_date} onChange={e=>setForm({...form, event_date:e.target.value})}/></Field>
          <Field label={t("venue")}><Input required value={form.venue} onChange={e=>setForm({...form, venue:e.target.value})}/></Field>
          <Field label={t("organisingUnit")}><Input value={form.organising_unit} onChange={e=>setForm({...form, organising_unit:e.target.value})}/></Field>
          <Field label={t("estimatedAttendance")}><Input type="number" value={form.estimated_attendance} onChange={e=>setForm({...form, estimated_attendance:parseInt(e.target.value||"0")})}/></Field>
          <Field label={t("description")} full><Textarea rows={3} value={form.description} onChange={e=>setForm({...form, description:e.target.value})}/></Field>
        </Section>
        <LocalityFields form={form} setForm={setForm} districts={districts} acs={acs} testPrefix="ev-"/>
        <div className="flex gap-2">
          <Button data-testid="ev-save" type="submit">{t("save")}</Button>
          <Button type="button" variant="outline" onClick={()=>nav("/events")}>{t("cancel")}</Button>
        </div>
        {details?.invitations && !isNew && (
          <InvitationsPanel invitations={details.invitations} eventName={details.event_name}/>
        )}
        {details?.participations?.length > 0 && (
          <div>
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Participants ({details.participations.length})</h3>
            <div className="border border-border bg-card">
              <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>District</TableHead><TableHead>{t("invitationStatus")}</TableHead><TableHead>{t("nature")}</TableHead><TableHead>{t("whatsapp")}</TableHead></TableRow></TableHeader>
                <TableBody>{details.participations.map(p=>{
                  const contact = (p.influencer?.contact_number || "").replace(/\D/g, "");
                  const waNum = contact.length === 10 ? `91${contact}` : contact;
                  const enMsg = details.invitations?.english || "";
                  const bnMsg = details.invitations?.bengali || "";
                  return (
                    <TableRow key={p.id}>
                      <TableCell><Link to={`/influencers/${p.influencer_id}`} className="underline">{p.influencer?.name}</Link></TableCell>
                      <TableCell>{p.influencer?.district}</TableCell>
                      <TableCell><Badge>{p.invitation_status}</Badge></TableCell>
                      <TableCell>{p.nature_of_participation}</TableCell>
                      <TableCell>
                        {waNum ? <div className="flex gap-1">
                          <a data-testid={`wa-en-${p.id}`} href={`https://wa.me/${waNum}?text=${encodeURIComponent(enMsg)}`} target="_blank" rel="noreferrer" className="px-2 py-1 text-xs bg-primary text-primary-foreground hover:opacity-90 inline-flex items-center gap-1"><MessageCircle className="w-3 h-3"/>EN</a>
                          <a data-testid={`wa-bn-${p.id}`} href={`https://wa.me/${waNum}?text=${encodeURIComponent(bnMsg)}`} target="_blank" rel="noreferrer" className="px-2 py-1 text-xs bg-accent text-accent-foreground hover:opacity-90 inline-flex items-center gap-1"><MessageCircle className="w-3 h-3"/>BN</a>
                        </div> : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}</TableBody></Table>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

// --- Categories ---
function CategoriesPage() {
  const { t } = useT();
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({name:"", parent_id:""});
  const load = () => axios.get(`${API}/categories`).then(r=>setCats(r.data));
  useEffect(() => { load(); }, []);
  const rootCats = cats.filter(c=>!c.parent_id);
  const submit = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/categories`, {name: form.name, parent_id: form.parent_id || null}); toast.success("Added"); setForm({name:"", parent_id:""}); load(); }
    catch(er){ toast.error(errMsg(er)); }
  };
  const del = async (id) => { try { await axios.delete(`${API}/categories/${id}`); toast.success("Deleted"); load(); } catch(e){ toast.error(errMsg(e)); } };
  const canManage = user?.role === "super_admin" || user?.role === "state_admin";
  return (
    <div>
      <PageHeader title={t("categories")} subtitle={`${cats.length} entries`}/>
      <div className="p-4 md:p-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {rootCats.map(c=>(
            <div key={c.id} className="border border-border bg-card p-4">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-base">{c.name}</div>
                {canManage && <button data-testid={`del-cat-${c.id}`} onClick={()=>del(c.id)} className="text-xs text-destructive uppercase tracking-widest">{t("delete")}</button>}
              </div>
              <div className="flex gap-2 flex-wrap mt-3">
                {cats.filter(s=>s.parent_id===c.id).map(s=><Badge key={s.id} variant="outline">{s.name}</Badge>)}
              </div>
            </div>
          ))}
        </div>
        {canManage && <form onSubmit={submit} className="border border-border bg-card p-6 h-fit space-y-3">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground">{t("addCategory")}</h3>
          <div><Label>{t("name")}</Label><Input data-testid="cat-name" required value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
          <div><Label>{t("parentCategory")}</Label>
            <Select value={form.parent_id || "none"} onValueChange={v=>setForm({...form, parent_id: v==="none"?"":v})}>
              <SelectTrigger><SelectValue placeholder={t("none")}/></SelectTrigger>
              <SelectContent><SelectItem value="none">{t("none")}</SelectItem>{rootCats.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
          <Button data-testid="cat-add" type="submit" className="w-full">{t("add")}</Button>
        </form>}
      </div>
    </div>
  );
}

// --- Audit ---
function AuditPage() {
  const { t } = useT();
  const [logs, setLogs] = useState([]);
  useEffect(() => { axios.get(`${API}/audit`).then(r=>setLogs(r.data)).catch(e=>toast.error(errMsg(e))); }, []);
  return (
    <div>
      <PageHeader title={t("audit")} subtitle={`${logs.length} events`}/>
      <div className="p-4 md:p-8">
        <div className="border border-border bg-card overflow-x-auto">
          <Table><TableHeader><TableRow><TableHead>{t("timestamp")}</TableHead><TableHead>{t("user")}</TableHead><TableHead>{t("action")}</TableHead><TableHead>{t("target")}</TableHead><TableHead>{t("details")}</TableHead></TableRow></TableHeader>
            <TableBody>{logs.map(l=>(
              <TableRow key={l.id}><TableCell className="text-xs">{new Date(l.timestamp).toLocaleString()}</TableCell><TableCell>{l.user_email}</TableCell><TableCell><Badge variant="outline">{l.action}</Badge></TableCell><TableCell>{l.target_type} {l.target_id?.slice(0,8)}</TableCell><TableCell className="text-xs">{l.details}</TableCell></TableRow>
            ))}</TableBody></Table>
        </div>
      </div>
    </div>
  );
}

// --- Users ---
function SurveyAdminPage() {
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({total:0, by_municipality:{}});
  const [municipality, setMunicipality] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [detail, setDetail] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null); // single row
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const municipalities = [
    { id: "all", label: "All / সব" },
    { id: "kolkata", label: "Kolkata / কলকাতা" },
    { id: "howrah", label: "Howrah / হাওড়া" },
    { id: "asansol", label: "Asansol / আসানসোল" },
  ];
  const buildQuery = () => {
    const p = new URLSearchParams();
    if (municipality !== "all") p.set("municipality", municipality);
    if (fromDate) p.set("from_date", fromDate);
    if (toDate) p.set("to_date", toDate);
    const qs = p.toString();
    return qs ? `?${qs}` : "";
  };
  const load = async () => {
    setBusy(true);
    try {
      const qs = buildQuery();
      const [s, l] = await Promise.all([
        axios.get(`${API}/surveys/summary`).then(r=>r.data),
        axios.get(`${API}/surveys/submissions${qs}`).then(r=>r.data),
      ]);
      setSummary(s); setRows(l); setSelected(new Set());
    } catch(er) { toast.error(errMsg(er)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [municipality, fromDate, toDate]);
  const exportXlsx = async () => {
    try {
      let res;
      if (selected.size > 0) {
        res = await axios.post(`${API}/surveys/export.xlsx/selected`, { ids: Array.from(selected) }, { responseType: "blob" });
      } else {
        const qs = buildQuery();
        res = await axios.get(`${API}/surveys/export.xlsx${qs}`, { responseType: "blob" });
      }
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0,10);
      a.download = selected.size > 0 ? `surveys_selected_${selected.size}_${stamp}.xlsx` : `surveys_${municipality}_${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch(er) { toast.error(errMsg(er)); }
  };
  const doDelete = async () => {
    if (!deleteFor) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/surveys/submissions/${deleteFor.id}`);
      toast.success("Deleted");
      setDeleteFor(null);
      load();
    } catch(er) { toast.error(errMsg(er)); }
    finally { setDeleting(false); }
  };
  const doBulkDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const { data } = await axios.post(`${API}/surveys/submissions/bulk-delete`, { ids: Array.from(selected) });
      toast.success(`Deleted ${data.deleted} response${data.deleted===1?"":"s"}`);
      setBulkOpen(false);
      load();
    } catch(er) { toast.error(errMsg(er)); }
    finally { setDeleting(false); }
  };
  const toggleSel = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r=>r.id)));
  };
  const clearDates = () => { setFromDate(""); setToDate(""); };
  const setRange = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (days - 1));
    setFromDate(from.toISOString().slice(0,10));
    setToDate(to.toISOString().slice(0,10));
  };
  const setThisMonth = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    setFromDate(first.toISOString().slice(0,10));
    setToDate(now.toISOString().slice(0,10));
  };
  const rangePresets = [
    { id: "today", label: "Today / আজ", onClick: () => setRange(1) },
    { id: "7d", label: "Last 7 days", onClick: () => setRange(7) },
    { id: "30d", label: "Last 30 days", onClick: () => setRange(30) },
    { id: "month", label: "This month", onClick: setThisMonth },
    { id: "all", label: "All time", onClick: clearDates },
  ];
  return (
    <div>
      <PageHeader title={t("tileSurveyAdmin") || "Survey Admin"} subtitle={`${summary.total || 0} responses across all corporations`}/>
      <div className="p-4 md:p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {municipalities.map(m => (
            <div key={m.id} className={`border p-4 ${municipality===m.id?"border-primary bg-secondary":"border-border bg-card"}`}>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{m.label}</div>
              <div className="text-2xl font-black mt-1">{m.id==="all"?(summary.total||0):(summary.by_municipality?.[m.id]||0)}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 items-end border border-border bg-card p-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Municipality</div>
            <div className="flex gap-1 flex-wrap">
              {municipalities.map(m => (
                <button key={m.id} onClick={()=>setMunicipality(m.id)} data-testid={`survey-filter-${m.id}`}
                  className={`px-3 py-1.5 text-xs border ${municipality===m.id?"bg-primary text-primary-foreground border-primary":"border-border hover:bg-secondary"}`}>{m.label}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">From date</div>
            <Input type="date" data-testid="survey-from-date" value={fromDate} onChange={e=>setFromDate(e.target.value)} className="h-8 text-sm"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">To date</div>
            <Input type="date" data-testid="survey-to-date" value={toDate} onChange={e=>setToDate(e.target.value)} className="h-8 text-sm"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Quick range</div>
            <div className="flex gap-1 flex-wrap">
              {rangePresets.map(r => (
                <button key={r.id} onClick={r.onClick} data-testid={`survey-range-${r.id}`}
                  className="px-3 py-1.5 text-xs border border-border hover:bg-secondary">{r.label}</button>
              ))}
            </div>
          </div>
          <div className="flex-1"/>
          {selected.size > 0 && (
            <>
              <Button size="sm" variant="destructive" data-testid="survey-bulk-delete" onClick={()=>setBulkOpen(true)}>
                <Trash2 className="w-3 h-3 mr-1"/>Delete {selected.size} selected
              </Button>
              <Button size="sm" variant="outline" onClick={()=>setSelected(new Set())} data-testid="survey-clear-selection">Clear selection</Button>
            </>
          )}
          <Button data-testid="survey-export-xlsx" onClick={exportXlsx}>
            <Download className="w-3 h-3 mr-1"/>{selected.size > 0 ? `Export ${selected.size} selected` : "Export Excel"}
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={busy}>{busy?"…":"Refresh"}</Button>
        </div>
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-12">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" data-testid="survey-select-all" checked={rows.length>0 && selected.size===rows.length} onChange={toggleAll}/>
                  <span className="text-[10px] text-muted-foreground">{selected.size>0?`${selected.size}/${rows.length}`:"all"}</span>
                </label>
              </TableHead>
              <TableHead>Timestamp</TableHead>
              <TableHead>Municipality</TableHead>
              <TableHead>Ward / Booth</TableHead>
              <TableHead>Top Issue</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>{rows.map(r => {
              const pl = r.payload || {};
              return (
                <TableRow key={r.id}>
                  <TableCell><input type="checkbox" data-testid={`survey-select-${r.id}`} checked={selected.has(r.id)} onChange={()=>toggleSel(r.id)}/></TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{new Date(r.server_ts).toLocaleString()}</TableCell>
                  <TableCell><Badge>{r.municipality}</Badge></TableCell>
                  <TableCell className="text-xs">{pl.ward || "—"} / {pl.booth_no || pl.booth || "—"}</TableCell>
                  <TableCell className="text-xs max-w-[240px] truncate">{pl.top_issue || pl.issue_detail || "—"}</TableCell>
                  <TableCell className="text-[10px] text-muted-foreground truncate max-w-[120px]">{r.client_id || "—"}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={()=>setDetail(r)} data-testid={`survey-view-${r.id}`}>View</Button>
                      <Button size="sm" variant="destructive" onClick={()=>setDeleteFor(r)} data-testid={`survey-delete-${r.id}`}><Trash2 className="w-3 h-3"/></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}</TableBody>
          </Table>
          {rows.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No responses in this range.</div>}
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o)=>{ if(!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Response — {detail?.municipality} · {new Date(detail?.server_ts||Date.now()).toLocaleString()}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto border border-border">
            <Table>
              <TableHeader><TableRow><TableHead>Field</TableHead><TableHead>Value</TableHead></TableRow></TableHeader>
              <TableBody>
                {Object.entries(detail?.payload || {}).map(([k,v]) => (
                  <TableRow key={k}><TableCell className="text-xs font-semibold">{k}</TableCell><TableCell className="text-xs whitespace-pre-wrap break-all">{String(v)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button onClick={()=>setDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteFor} onOpenChange={(o)=>{ if(!o) setDeleteFor(null); }}>
        <DialogContent data-testid="survey-delete-dialog">
          <DialogHeader><DialogTitle>Delete response</DialogTitle></DialogHeader>
          <p className="text-sm">Delete this response ({deleteFor?.municipality}, {deleteFor && new Date(deleteFor.server_ts).toLocaleString()})? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDeleteFor(null)}>Cancel</Button>
            <Button variant="destructive" disabled={deleting} data-testid="survey-delete-confirm" onClick={doDelete}>{deleting?"…":"Delete"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="survey-bulk-dialog">
          <DialogHeader><DialogTitle>Delete {selected.size} responses</DialogTitle></DialogHeader>
          <p className="text-sm">This will permanently delete {selected.size} selected response{selected.size===1?"":"s"}. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setBulkOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={deleting} data-testid="survey-bulk-confirm" onClick={doBulkDelete}>{deleting?"…":`Delete ${selected.size}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Users ---
function UsersPage() {
  const { t } = useT();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const { districts } = useGeo();
  const [form, setForm] = useState({email:"", password:"", name:"", role:"district_admin", district:""});
  const [resetFor, setResetFor] = useState(null); // {id, email, name}
  const [resetPw, setResetPw] = useState("");
  const [resetResult, setResetResult] = useState(null); // {email, new_password}
  const [deleteFor, setDeleteFor] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const load = () => axios.get(`${API}/users`).then(r=>setUsers(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/users`, {...form, district: form.district || null}); toast.success("Created"); setForm({email:"", password:"", name:"", role:"district_admin", district:""}); load(); }
    catch(er) { toast.error(errMsg(er)); }
  };
  const doReset = async () => {
    try {
      const { data } = await axios.post(`${API}/users/${resetFor.id}/reset-password`, { new_password: resetPw || null });
      setResetResult(data);
      setResetPw("");
      toast.success("Password reset");
    } catch(er) { toast.error(errMsg(er)); }
  };
  const closeReset = () => { setResetFor(null); setResetPw(""); setResetResult(null); };
  const copyPw = async () => {
    try { await navigator.clipboard.writeText(resetResult.new_password); toast.success(t("copied")); } catch { toast.error("Copy failed"); }
  };
  const doDelete = async () => {
    if (!deleteFor) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/${deleteFor.id}`);
      toast.success("User deleted");
      setDeleteFor(null);
      load();
    } catch(er) { toast.error(errMsg(er)); }
    finally { setDeleting(false); }
  };
  const canManage = (u) => currentUser && u.id !== currentUser.id && (currentUser.role === "super_admin" || u.created_by === currentUser.id);
  return (
    <div>
      <PageHeader title={t("users")} subtitle={`${users.length} accounts`}/>
      <div className="p-4 md:p-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-border bg-card overflow-x-auto">
          <Table><TableHeader><TableRow><TableHead>{t("name")}</TableHead><TableHead>{t("email")}</TableHead><TableHead>{t("role")}</TableHead><TableHead>{t("district")}</TableHead><TableHead>{t("status")}</TableHead><TableHead>{t("action")}</TableHead></TableRow></TableHeader>
            <TableBody>{users.map(u=>(
              <TableRow key={u.id}>
                <TableCell className="font-semibold">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Badge>{u.role}</Badge></TableCell>
                <TableCell>{u.district||"—"}</TableCell>
                <TableCell>{u.is_active?"Active":"Inactive"}</TableCell>
                <TableCell>
                  {canManage(u) ? (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" data-testid={`reset-pw-${u.id}`} onClick={()=>{setResetFor(u); setResetResult(null); setResetPw("");}}>
                        <Key className="w-3 h-3 mr-1"/>{t("resetPassword")}
                      </Button>
                      <Button size="sm" variant="destructive" data-testid={`delete-user-${u.id}`} onClick={()=>setDeleteFor(u)}>
                        <Trash2 className="w-3 h-3 mr-1"/>{t("delete")}
                      </Button>
                    </div>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
              </TableRow>
            ))}</TableBody></Table>
        </div>
        <form onSubmit={submit} className="border border-border bg-card p-6 h-fit space-y-3">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground">{t("createUser")}</h3>
          <div><Label>{t("name")}</Label><Input data-testid="user-name" required value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
          <div><Label>{t("email")}</Label><Input data-testid="user-email" type="email" required value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/></div>
          <div><Label>{t("password")}</Label><Input data-testid="user-password" type="password" required value={form.password} onChange={e=>setForm({...form, password:e.target.value})}/></div>
          <div><Label>{t("role")}</Label>
            <Select value={form.role} onValueChange={v=>setForm({...form, role:v})}><SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{["super_admin","state_admin","district_admin","assembly_admin"].map(r=><SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
          {(form.role === "district_admin" || form.role === "assembly_admin") && <div><Label>{t("district")}</Label>
            <Select value={form.district || "none"} onValueChange={v=>setForm({...form, district: v==="none"?"":v})}>
              <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{districts.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>}
          <Button data-testid="user-add" type="submit" className="w-full">{t("add")}</Button>
        </form>
      </div>

      <Dialog open={!!resetFor} onOpenChange={(o)=>{ if(!o) closeReset(); }}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle>{t("resetPassword")} — {resetFor?.name}</DialogTitle></DialogHeader>
          {!resetResult ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Enter a new password below, or leave blank to auto-generate a strong one.</p>
              <div>
                <Label>{t("newPassword")}</Label>
                <Input data-testid="reset-pw-input" type="text" placeholder="(auto-generate)" value={resetPw} onChange={e=>setResetPw(e.target.value)}/>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm">Password reset for <b>{resetResult.email}</b>. Share this password securely — it will not be shown again.</p>
              <div className="border-2 border-accent bg-secondary p-4 flex items-center justify-between">
                <code data-testid="reset-pw-result" className="text-lg font-mono font-bold tracking-wide select-all">{resetResult.new_password}</code>
                <Button size="sm" variant="outline" onClick={copyPw} data-testid="copy-new-pw"><Copy className="w-3 h-3 mr-1"/>{t("copy")}</Button>
              </div>
            </div>
          )}
          <DialogFooter>
            {!resetResult
              ? <>
                  <Button variant="outline" onClick={closeReset}>{t("cancel")}</Button>
                  <Button data-testid="reset-pw-submit" onClick={doReset}>{t("resetPassword")}</Button>
                </>
              : <Button onClick={closeReset}>{t("close") || "Close"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteFor} onOpenChange={(o)=>{ if(!o) setDeleteFor(null); }}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader><DialogTitle>{t("delete")} — {deleteFor?.name}</DialogTitle></DialogHeader>
          <p className="text-sm">Are you sure you want to delete <b>{deleteFor?.email}</b>? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDeleteFor(null)}>{t("cancel")}</Button>
            <Button variant="destructive" disabled={deleting} data-testid="delete-user-confirm" onClick={doDelete}>{deleting?"…":t("delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Forgot / Reset Password ---
function AuthShell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 30%, rgba(212,175,55,0.3), transparent 50%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.15), transparent 50%)'}}/>
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 border-2 border-accent flex items-center justify-center bg-white"><CPIMLogo className="w-10 h-10"/></div>
            <span className="tracking-widest text-xs uppercase text-accent">Restricted · Party Internal</span>
          </div>
          <h1 className="mt-16 text-5xl lg:text-6xl font-black leading-tight tracking-tight">Local Influencer Database</h1>
        </div>
        <div className="relative z-10 text-xs uppercase tracking-widest opacity-70">© CPI(M) West Bengal</div>
      </div>
      <div className="flex items-center justify-center p-8">{children}</div>
    </div>
  );
}

function ForgotPasswordPage() {
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const loc = useLocation();
  const module = new URLSearchParams(loc.search).get("module") === "baithak" ? "baithak" : "lid";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await axios.post(`${API}/auth/forgot-password`, { email, module }); setSent(true); }
    catch(er) { toast.error(errMsg(er)); }
    finally { setBusy(false); }
  };
  return (
    <AuthShell>
      <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="forgot-pw-form">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t("forgotPassword") || "Forgot Password?"}</h2>
          <p className="text-sm text-muted-foreground mt-2">Enter your registered email and we&apos;ll send you a secure link to reset your password.</p>
        </div>
        {!sent ? <>
          <div><Label>{t("email")}</Label><Input data-testid="fp-email" type="email" required value={email} onChange={e=>setEmail(e.target.value)}/></div>
          <Button data-testid="fp-submit" type="submit" disabled={busy} className="w-full h-11">{busy?"...":t("sendResetLink")||"Send Reset Link"}</Button>
        </> : (
          <div className="border-l-4 border-accent bg-secondary p-4">
            <p className="text-sm">If an account exists with <b>{email}</b>, a password reset link has been sent. The link is valid for 30 minutes.</p>
          </div>
        )}
        <div className="text-center">
          <Link to="/login" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary underline underline-offset-4">{t("backToLogin") || "Back to Login"}</Link>
        </div>
      </form>
    </AuthShell>
  );
}

function ResetPasswordPage() {
  const { token } = useParams();
  const { t } = useT();
  const nav = useNavigate();
  const loc = useLocation();
  const isBaithak = loc.pathname.startsWith("/baithaks/");
  const loginPath = isBaithak ? "/baithaks/login" : "/login";
  const [status, setStatus] = useState("checking"); // checking|valid|invalid|done
  const [email, setEmail] = useState("");
  const [np, setNp] = useState("");
  const [conf, setConf] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/auth/reset-password/${token}`)
      .then(r => { if (r.data.valid) { setEmail(r.data.email || ""); setStatus("valid"); } else setStatus("invalid"); })
      .catch(() => setStatus("invalid"));
  }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    if (np.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (np !== conf) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try { await axios.post(`${API}/auth/reset-password/confirm`, { token, new_password: np }); setStatus("done"); }
    catch(er) { toast.error(errMsg(er)); }
    finally { setBusy(false); }
  };
  return (
    <AuthShell>
      <div className="w-full max-w-sm space-y-6" data-testid="reset-pw-page">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t("resetPassword")}</h2>
          {status==="valid" && <p className="text-sm text-muted-foreground mt-2">Set a new password for <b>{email}</b>.</p>}
        </div>
        {status==="checking" && <p className="text-sm text-muted-foreground">Verifying link…</p>}
        {status==="invalid" && (
          <div className="border-l-4 border-destructive bg-secondary p-4">
            <p className="text-sm font-semibold">This reset link is invalid or has expired.</p>
            <p className="text-xs text-muted-foreground mt-2">Please request a new one from the login page.</p>
          </div>
        )}
        {status==="valid" && (
          <form onSubmit={submit} className="space-y-4">
            <div><Label>{t("newPassword") || "New Password"}</Label><Input data-testid="rp-new" type="password" required value={np} onChange={e=>setNp(e.target.value)}/></div>
            <div><Label>{t("confirmPassword") || "Confirm Password"}</Label><Input data-testid="rp-confirm" type="password" required value={conf} onChange={e=>setConf(e.target.value)}/></div>
            <Button data-testid="rp-submit" type="submit" disabled={busy} className="w-full h-11">{busy?"...":t("resetPassword")}</Button>
          </form>
        )}
        {status==="done" && (
          <div className="border-l-4 border-accent bg-secondary p-4">
            <p className="text-sm font-semibold">Password reset successful.</p>
            <p className="text-xs text-muted-foreground mt-2">You can now sign in with your new password.</p>
            <Button className="mt-3 w-full" onClick={()=>nav(loginPath)}>{t("backToLogin") || "Back to Login"}</Button>
          </div>
        )}
        {status !== "done" && (
          <div className="text-center">
            <Link to={loginPath} className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary underline underline-offset-4">{t("backToLogin") || "Back to Login"}</Link>
          </div>
        )}
      </div>
    </AuthShell>
  );
}

// --- Para Baithak ---
function BaithakLayout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useT();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);
  const canManageUsers = ["super_admin", "state_admin", "district_admin"].includes(user?.role);
  const items = [
    { to: "/baithaks", label: t("baithakRecords") || "All Baithaks", icon: Calendar },
    { to: "/baithaks/new", label: t("newBaithak") || "New Baithak", icon: Plus },
  ];
  if (canManageUsers) items.push({ to: "/baithaks/users", label: t("manageUsers") || "Manage Users", icon: Shield });
  const inner = (
    <>
      <div className="p-4 md:p-6 border-b border-border flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 bg-white flex items-center justify-center border border-accent"><CPIMLogo className="w-7 h-7"/></div>
          <div>
            <div className="font-black tracking-tight text-sm leading-tight">Para Baithak</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">CPI(M) West Bengal</div>
          </div>
        </Link>
        <button className="lg:hidden text-muted-foreground p-1" onClick={()=>setMobileOpen(false)}><X className="w-5 h-5"/></button>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {items.map(it => {
          const active = loc.pathname === it.to;
          const Icon = it.icon;
          return (
            <Link key={it.to} to={it.to} data-testid={`bnav-${it.to.split("/").pop() || "list"}`}
              className={`flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 ${active ? "bg-secondary text-foreground border-accent font-semibold" : "text-muted-foreground border-transparent hover:text-foreground hover:bg-secondary/50"}`}>
              <Icon className="w-4 h-4"/> {it.label}
            </Link>
          );
        })}
        <div className="pt-4 mt-4 border-t border-border">
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground">
            <ChevronRight className="w-4 h-4 rotate-180"/> {t("backToPortal") || "Back to Portal"}
          </Link>
        </div>
      </nav>
      <div className="p-4 border-t border-border">
        <Button data-testid="change-pw-btn" variant="outline" size="sm" className="w-full mb-2" onClick={()=>setChangePwOpen(true)}><Key className="w-3 h-3 mr-1"/>{t("changePassword")}</Button>
        <div className="flex gap-1 mb-3 text-xs">
          <button type="button" data-testid="lang-en" onClick={()=>setLang("en")} className={`flex-1 px-2 py-1 border ${lang==="en"?"bg-primary text-primary-foreground border-primary":"border-border"}`}>EN</button>
          <button type="button" data-testid="lang-bn" onClick={()=>setLang("bn")} className={`flex-1 px-2 py-1 border ${lang==="bn"?"bg-primary text-primary-foreground border-primary":"border-border"}`}>বাংলা</button>
        </div>
        <div className="text-xs">
          <div className="font-semibold truncate">{user?.name}</div>
          <div className="text-muted-foreground uppercase tracking-widest text-[10px]">{user?.role?.replace("_"," ")}</div>
          {user?.district && <div className="text-muted-foreground text-[10px]">{user.district}{user?.ac_name?` · ${user.ac_name}`:""}</div>}
        </div>
        <Button variant="outline" size="sm" data-testid="logout-btn" className="w-full mt-3" onClick={()=>{logout(); nav("/login");}}><LogOut className="w-3 h-3 mr-1"/>{t("logout")}</Button>
      </div>
    </>
  );
  return (
    <div className={`min-h-screen bg-background lg:flex ${lang==="bn"?"font-bn":""}`}>
      <div className="lg:hidden sticky top-0 z-30 bg-card border-b border-border flex items-center justify-between px-4 py-3">
        <button onClick={()=>setMobileOpen(true)} className="p-1"><Menu className="w-6 h-6"/></button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-white flex items-center justify-center border border-accent"><CPIMLogo className="w-5 h-5"/></div>
          <div className="font-black text-xs tracking-tight">Para Baithak</div>
        </div>
        <div className="w-8"/>
      </div>
      {mobileOpen && <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={()=>setMobileOpen(false)}/>}
      <aside className={`fixed lg:static top-0 left-0 z-50 h-full w-72 lg:w-64 border-r border-border bg-card flex flex-col transition-transform lg:transition-none ${mobileOpen?"translate-x-0":"-translate-x-full lg:translate-x-0"}`}>{inner}</aside>
      <main className="flex-1 min-w-0 overflow-x-hidden">{children}</main>
      <ChangePasswordDialog open={changePwOpen} onClose={()=>setChangePwOpen(false)}/>
    </div>
  );
}

function BaithakListPage() {
  const [data, setData] = useState({total:0, items:[]});
  const [q, setQ] = useState("");
  const [district, setDistrict] = useState("all");
  const { districts } = useGeo();
  const { t } = useT();
  const nav = useNavigate();
  const load = useCallback(() => {
    const p = {};
    if (q) p.q = q;
    if (district !== "all") p.district = district;
    axios.get(`${API}/baithaks`, { params: p }).then(r=>setData(r.data));
  }, [q, district]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <PageHeader title={t("baithakRecords")} subtitle={`${data.total} ${t("baithakRecords")||"records"}`}
        action={<div className="flex gap-2 flex-wrap">
          <Button variant="outline" data-testid="export-baithak-btn" onClick={()=>downloadCSV("/export/baithaks.xlsx", "baithaks.xlsx", { ...(q?{q}:{}), ...(district!=="all"?{district}:{}) })}><Download className="w-4 h-4 mr-1"/>{t("exportExcel")}</Button>
          <Button data-testid="new-baithak-btn" onClick={()=>nav("/baithaks/new")}><Plus className="w-4 h-4 mr-1"/>{t("newBaithak")}</Button>
        </div>}/>
      <div className="p-4 md:p-8 flex flex-col lg:flex-row gap-6">
        <aside className="w-full lg:w-64 shrink-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-3 lg:space-y-4 lg:block">
          <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1 md:col-span-2 lg:col-span-1"><Filter className="w-3 h-3"/>{t("filter")}</div>
          <div><Label>{t("search")}</Label><Input data-testid="baithak-search" value={q} onChange={e=>setQ(e.target.value)}/></div>
          <div><Label>{t("district")}</Label>
            <Select value={district} onValueChange={setDistrict}><SelectTrigger data-testid="baithak-filter-district"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="all">{t("allDistricts")}</SelectItem>{districts.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
        </aside>
        <div className="flex-1 min-w-0 border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>{t("district")}</TableHead><TableHead>{t("ac")}</TableHead><TableHead>{t("para")}</TableHead><TableHead>{t("localBody")}</TableHead><TableHead>{t("dateTime")}</TableHead><TableHead>{t("attendance")}</TableHead><TableHead>{t("by")}</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {data.items.length===0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">{t("noBaithaks")}</TableCell></TableRow>}
              {data.items.map(b=>(
                <TableRow key={b.id} data-testid={`baithak-row-${b.id}`} className="cursor-pointer" onClick={()=>nav(`/baithaks/${b.id}`)}>
                  <TableCell className="font-semibold">{b.district}</TableCell>
                  <TableCell>{b.ac_name}</TableCell>
                  <TableCell className="text-xs">{b.para_name||"—"}</TableCell>
                  <TableCell className="text-xs">{b.local_body_type==="municipality"?`${b.municipality==="Other"?(b.other_location||"Other"):(b.municipality||"")}${b.ward?` · ${b.ward==="Other"?"Ward Other":`Ward ${b.ward}`}`:""}${b.para?` · ${b.para}`:""}`:b.local_body_type==="panchayat"?`${b.panchayat==="Other"?(b.other_location||"Other"):(b.panchayat||"")}${b.village?` · ${b.village==="Other"?"Village Other":b.village}`:""}${b.para?` · ${b.para}`:""}`:b.other_location||"—"}</TableCell>
                  <TableCell className="text-xs">{b.date_time?new Date(b.date_time).toLocaleString():""}</TableCell>
                  <TableCell>{b.total_count}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{b.created_by_name}</TableCell>
                  <TableCell><ChevronRight className="w-4 h-4"/></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function BaithakForm() {
  const { id } = useParams();
  const loc = useLocation();
  const isNew = !id || loc.pathname.endsWith("/new");
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useT();
  const { districts, acs } = useGeo();
  const [munis, setMunis] = useState([]);
  const [panchs, setPanchs] = useState([]);
  const [form, setForm] = useState({
    date_time: new Date().toISOString().slice(0,16), para_name:"", district: user?.district || "",
    ac_no: user?.ac_no || null, ac_name: user?.ac_name || "",
    local_body_type: "panchayat", municipality:"", ward:"", panchayat:"", village:"", para:"",
    other_location:"", male_count:0, female_count:0, total_count:0,
    attendees:[], minutes:"", photo_base64:"", photo_lat:null, photo_lng:null, photo_source:""
  });
  useEffect(() => {
    if (isNew || !id) return;
    axios.get(`${API}/baithaks/${id}`).then(r=>{
      const d = r.data;
      d.date_time = d.date_time ? d.date_time.slice(0,16) : "";
      setForm(d);
    });
  }, [id, isNew]);
  useEffect(() => {
    if (form.ac_no==null) { setMunis([]); setPanchs([]); return; }
    axios.get(`${API}/geo/municipalities`, { params: { ac_no: form.ac_no } }).then(r=>setMunis(r.data));
    axios.get(`${API}/geo/panchayats`, { params: { ac_no: form.ac_no } }).then(r=>setPanchs(r.data));
  }, [form.ac_no]);
  const acsFiltered = form.district ? acs.filter(a=>a.district===form.district) : acs;
  const selectedMun = munis.find(m=>m.municipality===form.municipality);
  const selectedPan = panchs.find(p=>p.panchayat===form.panchayat);

  const [capturingLoc, setCapturingLoc] = useState(false);
  const captureLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported by this browser"); return; }
    setCapturingLoc(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setForm(prev => ({ ...prev, photo_lat: pos.coords.latitude, photo_lng: pos.coords.longitude, photo_source: "device" }));
        toast.success("Location captured");
        setCapturingLoc(false);
      },
      err => { toast.error(`Location error: ${err.message}`); setCapturingLoc(false); },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3000000) { toast.error("Photo too large (>3MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setForm(prev => ({ ...prev, photo_base64: reader.result }));
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => setForm(prev => ({ ...prev, photo_lat: pos.coords.latitude, photo_lng: pos.coords.longitude, photo_source: "device" })),
          () => {},
          { enableHighAccuracy: true, timeout: 8000 }
        );
      }
    };
    reader.readAsDataURL(f);
  };

  const addAttendee = () => setForm(p=>({...p, attendees:[...p.attendees, {name:"", sex:"", phone:"", social_status:""}]}));
  const removeAttendee = (i) => setForm(p=>({...p, attendees: p.attendees.filter((_,idx)=>idx!==i)}));
  const updateAttendee = (i, field, val) => setForm(p=>{ const a=[...p.attendees]; a[i]={...a[i], [field]:val}; return {...p, attendees:a}; });

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {...form, total_count: Number(form.male_count||0)+Number(form.female_count||0)};
    try {
      if (isNew) { await axios.post(`${API}/baithaks`, payload); toast.success("Baithak recorded"); nav("/baithaks"); }
      else { await axios.patch(`${API}/baithaks/${id}`, payload); toast.success("Updated"); nav("/baithaks"); }
    } catch(er) { toast.error(errMsg(er)); }
  };

  return (
    <div>
      <PageHeader title={isNew?t("newBaithak"):t("editBaithak")} subtitle={t("tileBaithakTitle")}/>
      <form onSubmit={onSubmit} className="p-4 md:p-8 max-w-5xl space-y-6 md:space-y-8" data-testid="baithak-form">
        <Section title={t("timeLocation")}>
          <Field label={t("dateTime")}><Input type="datetime-local" required value={form.date_time} onChange={e=>setForm({...form, date_time:e.target.value})} data-testid="b-datetime"/></Field>
          <Field label={t("paraName")}><Input required value={form.para_name} onChange={e=>setForm({...form, para_name:e.target.value})} data-testid="b-para-name" placeholder={t("paraPlaceholder")}/></Field>
          <Field label={t("district")}>
            <Select value={form.district||"none"} onValueChange={v=>setForm({...form, district:v==="none"?"":v, ac_no:null, ac_name:"", municipality:"", ward:"", panchayat:"", village:""})}>
              <SelectTrigger data-testid="b-district"><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent>{districts.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label={t("ac")}>
            <Select value={form.ac_no?String(form.ac_no):"none"} onValueChange={v=>{ if(v==="none"){setForm({...form, ac_no:null, ac_name:"", municipality:"", ward:"", panchayat:"", village:""}); return;} const ac=acsFiltered.find(a=>String(a.ac_no)===v); setForm({...form, ac_no:ac?.ac_no, ac_name:ac?.ac_name||"", municipality:"", ward:"", panchayat:"", village:""});}}>
              <SelectTrigger data-testid="b-ac"><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{acsFiltered.map(a=><SelectItem key={a.ac_no} value={String(a.ac_no)}>{a.ac_name}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label={t("localBodyType")||"Local Body Type"} full>
            <div className="flex gap-2 flex-wrap">
              {["panchayat","municipality"].map(tp=>(
                <button key={tp} type="button" data-testid={`b-lb-${tp}`} onClick={()=>setForm({...form, local_body_type:tp, municipality:"", ward:"", panchayat:"", village:"", other_location:""})} className={`px-3 py-2 text-sm border capitalize ${form.local_body_type===tp?"bg-primary text-primary-foreground border-primary":"border-border hover:bg-secondary"}`}>
                  {tp==="panchayat"?(t("gramPanchayat")||"Gram Panchayat"):(t("municipality")||"Municipality")}
                </button>
              ))}
            </div>
          </Field>
          {form.local_body_type==="municipality" && <>
            <Field label={t("municipality")||"Municipality"}>
              <Select value={form.municipality||"none"} onValueChange={v=>setForm({...form, municipality:v==="none"?"":v, ward:""})} disabled={!form.ac_no}>
                <SelectTrigger data-testid="b-mun"><SelectValue placeholder={form.ac_no?"—":"Select AC first"}/></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{munis.map(m=><SelectItem key={m.id} value={m.municipality}>{m.municipality}</SelectItem>)}<SelectItem value="Other">{t("other")||"Other"}</SelectItem></SelectContent></Select>
              {form.municipality==="Other" && <Input className="mt-2" placeholder={t("specifyMunicipality")||"Specify municipality name"} value={form.other_location} onChange={e=>setForm({...form, other_location:e.target.value})} data-testid="b-mun-other"/>}
            </Field>
            <Field label={t("wardOptional")||"Ward No (optional)"}>
              <Select value={form.ward||"none"} onValueChange={v=>setForm({...form, ward:v==="none"?"":v})} disabled={!selectedMun}>
                <SelectTrigger data-testid="b-ward"><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{(selectedMun?.wards||[]).map(w=><SelectItem key={w} value={w}>{(t("ward")||"Ward")} {w}</SelectItem>)}<SelectItem value="Other">{t("other")||"Other"}</SelectItem></SelectContent></Select>
            </Field>
          </>}
          {form.local_body_type==="panchayat" && <>
            <Field label={t("gramPanchayat")||"Gram Panchayat"}>
              <Select value={form.panchayat||"none"} onValueChange={v=>setForm({...form, panchayat:v==="none"?"":v, village:""})} disabled={!form.ac_no}>
                <SelectTrigger data-testid="b-pan"><SelectValue placeholder={form.ac_no?"—":"Select AC first"}/></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{panchs.map(p=><SelectItem key={p.id} value={p.panchayat}>{p.panchayat}</SelectItem>)}<SelectItem value="Other">{t("other")||"Other"}</SelectItem></SelectContent></Select>
              {form.panchayat==="Other" && <Input className="mt-2" placeholder={t("specifyPanchayat")||"Specify Panchayat name"} value={form.other_location} onChange={e=>setForm({...form, other_location:e.target.value})} data-testid="b-pan-other"/>}
            </Field>
            <Field label={t("villageOptional")||"Village (optional)"}>
              <Select value={form.village||"none"} onValueChange={v=>setForm({...form, village:v==="none"?"":v})} disabled={!selectedPan}>
                <SelectTrigger data-testid="b-vil"><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{(selectedPan?.villages||[]).map(v=><SelectItem key={v} value={v}>{v}</SelectItem>)}<SelectItem value="Other">{t("other")||"Other"}</SelectItem></SelectContent></Select>
            </Field>
          </>}
          <Field label={t("paraOptional")||"Para / Neighbourhood (optional)"} full>
            <Input value={form.para} onChange={e=>setForm({...form, para:e.target.value})} placeholder={t("paraPlaceholder")||"e.g. Uttar Para"} data-testid="b-para"/>
          </Field>
        </Section>

        <Section title={t("attendance")}>
          <Field label={t("maleCount")}><Input type="number" min="0" value={form.male_count} onChange={e=>setForm({...form, male_count:parseInt(e.target.value||"0")})} data-testid="b-male"/></Field>
          <Field label={t("femaleCount")}><Input type="number" min="0" value={form.female_count} onChange={e=>setForm({...form, female_count:parseInt(e.target.value||"0")})} data-testid="b-female"/></Field>
          <div className="md:col-span-2 text-xs text-muted-foreground">{t("total")}: <b>{Number(form.male_count||0)+Number(form.female_count||0)}</b></div>
        </Section>

        <section className="border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground">{t("attendeeDetails")}</h3>
            <Button type="button" size="sm" variant="outline" onClick={addAttendee} data-testid="b-add-attendee"><Plus className="w-3 h-3 mr-1"/>{t("add")}</Button>
          </div>
          {form.attendees.length===0 && <p className="text-sm text-muted-foreground">{t("noResults")}</p>}
          <div className="space-y-2">
            {form.attendees.map((a, i)=>(
              <div key={i} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end border-b border-border pb-2">
                <div><Label className="text-xs">{t("name")}</Label><Input value={a.name} onChange={e=>updateAttendee(i,"name",e.target.value)} required data-testid={`b-att-name-${i}`}/></div>
                <div><Label className="text-xs">{t("attendeeSex")}</Label>
                  <Select value={a.sex||"none"} onValueChange={v=>updateAttendee(i,"sex",v==="none"?"":v)}>
                    <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">{t("other")}</SelectItem></SelectContent></Select>
                </div>
                <div><Label className="text-xs">{t("attendeePhone")}</Label><Input value={a.phone} onChange={e=>updateAttendee(i,"phone",e.target.value)}/></div>
                <div><Label className="text-xs">{t("socialStatus")}</Label><Input value={a.social_status} onChange={e=>updateAttendee(i,"social_status",e.target.value)}/></div>
                <Button type="button" size="sm" variant="outline" className="text-destructive" onClick={()=>removeAttendee(i)}><Trash2 className="w-3 h-3"/></Button>
              </div>
            ))}
          </div>
        </section>

        <Section title={t("minutesAndPhoto")}>
          <Field label={t("meetingMinutes")} full><Textarea rows={6} required value={form.minutes} onChange={e=>setForm({...form, minutes:e.target.value})} data-testid="b-minutes"/></Field>
          <Field label={t("meetingPhoto")} full>
            <div className="flex flex-wrap gap-2 items-center">
              <label className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest border border-primary bg-primary text-primary-foreground cursor-pointer hover:opacity-90">
                <MessageCircle className="w-3.5 h-3.5"/> {t("takePhoto")}
                <input type="file" accept="image/*" capture="environment" onChange={onPhoto} data-testid="b-photo-camera" className="hidden"/>
              </label>
              <label className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest border border-border cursor-pointer hover:bg-secondary">
                <Plus className="w-3.5 h-3.5"/> {t("fromGallery")}
                <input type="file" accept="image/*" onChange={onPhoto} data-testid="b-photo-gallery" className="hidden"/>
              </label>
              <button type="button" onClick={captureLocation} disabled={capturingLoc} data-testid="b-capture-loc" className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest border border-accent text-accent-foreground bg-accent hover:opacity-90 disabled:opacity-50">
                <MapPin className="w-3.5 h-3.5"/> {capturingLoc?t("capturing"):t("captureLiveLocation")}
              </button>
            </div>
            {form.photo_base64 && (
              <div className="mt-3 flex gap-3 items-start">
                <img src={form.photo_base64} alt="preview" className="w-40 h-40 object-cover border border-border"/>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>{(form.photo_base64.length/1024).toFixed(0)} KB</div>
                  <button type="button" onClick={()=>setForm(p=>({...p, photo_base64:""}))} className="text-destructive underline">{t("removePhoto")}</button>
                </div>
              </div>
            )}
            {form.photo_lat != null && (
              <div className="mt-3 text-xs border-l-4 border-accent bg-secondary px-3 py-2">
                <div className="font-semibold">{t("locationCaptured")}</div>
                <div className="text-muted-foreground">Lat {form.photo_lat.toFixed(5)}, Lng {form.photo_lng.toFixed(5)} · <a target="_blank" rel="noreferrer" className="underline" href={`https://www.google.com/maps?q=${form.photo_lat},${form.photo_lng}`}>{t("openInMaps")}</a></div>
              </div>
            )}
            <div className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">{t("photoTip")}</div>
          </Field>
        </Section>

        <div className="flex gap-2">
          <Button type="submit" data-testid="b-save">{t("saveBaithak")}</Button>
          <Button type="button" variant="outline" onClick={()=>nav("/baithaks")}>{t("cancel")}</Button>
        </div>
      </form>
    </div>
  );
}

function BaithakDetailPage() {
  const { id } = useParams();
  const { t } = useT();
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { axios.get(`${API}/baithaks/${id}`).then(r=>setD(r.data)).catch(e=>{ toast.error(errMsg(e)); nav("/baithaks"); }); }, [id, nav]);
  if (!d) return <div className="p-8">Loading…</div>;
  const loc_str = d.local_body_type==="municipality" ? `${d.municipality==="Other"?(d.other_location||"Other"):(d.municipality||"")}${d.ward?` · Ward ${d.ward}`:""}${d.para?` · ${d.para}`:""}` : d.local_body_type==="panchayat" ? `${d.panchayat==="Other"?(d.other_location||"Other"):(d.panchayat||"")}${d.village?` · ${d.village}`:""}${d.para?` · ${d.para}`:""}` : d.other_location||"—";
  return (
    <div>
      <PageHeader title={`${d.district} · ${d.ac_name}`} subtitle={`${d.para_name?d.para_name+" · ":""}${new Date(d.date_time).toLocaleString()}`}
        action={<Button variant="outline" onClick={()=>nav(`/baithaks/${id}/edit`)}><Edit className="w-4 h-4 mr-1"/>Edit</Button>}/>
      <div className="p-4 md:p-8 max-w-5xl space-y-6" data-testid="baithak-detail">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-border bg-card p-4"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("location")}</div><div className="mt-1 text-sm font-semibold">{loc_str}</div></div>
          <div className="border border-border bg-card p-4"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Attendance</div><div className="mt-1 text-sm font-semibold">{d.total_count} total · {d.male_count} M · {d.female_count} F</div></div>
          <div className="border border-border bg-card p-4"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Recorded By</div><div className="mt-1 text-sm font-semibold">{d.created_by_name||"—"}</div></div>
        </div>
        {d.photo_base64 && <div className="border border-border bg-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Photograph</div>
          <img src={d.photo_base64} alt="baithak" className="max-w-lg max-h-80 object-contain border border-border"/>
        </div>}
        {d.photo_lat!=null && <div className="border border-border bg-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Location</div>
          <div className="text-sm">Lat {d.photo_lat.toFixed(5)}, Lng {d.photo_lng.toFixed(5)} · <a href={`https://www.google.com/maps?q=${d.photo_lat},${d.photo_lng}`} target="_blank" rel="noreferrer" className="underline">Open in Maps</a></div>
        </div>}
        <div className="border border-border bg-card p-6">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Minutes</div>
          <div className="text-sm whitespace-pre-wrap leading-relaxed">{d.minutes||"—"}</div>
        </div>
        {d.attendees?.length>0 && <div className="border border-border bg-card">
          <div className="p-4 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground">Attendees ({d.attendees.length})</div>
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Sex</TableHead><TableHead>Phone</TableHead><TableHead>Social Status</TableHead></TableRow></TableHeader>
            <TableBody>{d.attendees.map((a,i)=>(
              <TableRow key={i}><TableCell className="font-semibold">{a.name}</TableCell><TableCell>{a.sex}</TableCell><TableCell>{a.phone}</TableCell><TableCell>{a.social_status}</TableCell></TableRow>
            ))}</TableBody>
          </Table>
        </div>}
      </div>
    </div>
  );
}

function BaithakUsersPage() {
  const [users, setUsers] = useState([]);
  const { user } = useAuth();
  const { t } = useT();
  const { districts, acs } = useGeo();
  const [form, setForm] = useState({name:"", email:"", phone:"", password:"", role:"assembly_admin", district:"", ac_no:null, ac_name:""});
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetResult, setResetResult] = useState(null);
  const load = () => axios.get(`${API}/users`).then(r=>setUsers(r.data));
  useEffect(() => { load(); }, []);
  const allowedRoles = user?.role === "super_admin" ? ["state_admin","district_admin","assembly_admin"]
    : user?.role === "state_admin" ? ["district_admin","assembly_admin"]
    : user?.role === "district_admin" ? ["assembly_admin"] : [];
  const acsFiltered = form.district ? acs.filter(a=>a.district===form.district) : [];
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {...form};
      if (payload.role === "assembly_admin") { const ac = acsFiltered.find(a=>String(a.ac_no)===String(payload.ac_no)); payload.ac_name = ac?.ac_name || ""; }
      await axios.post(`${API}/users`, payload);
      toast.success("User created");
      setForm({name:"", email:"", phone:"", password:"", role:"assembly_admin", district: user?.role==="district_admin"?user.district:"", ac_no:null, ac_name:""});
      load();
    } catch(er) { toast.error(errMsg(er)); }
  };
  const doReset = async () => {
    try {
      const { data } = await axios.post(`${API}/users/${resetFor.id}/reset-password`, { new_password: resetPw || null });
      setResetResult(data);
      setResetPw("");
      toast.success("Password reset");
    } catch(er) { toast.error(errMsg(er)); }
  };
  const closeReset = () => { setResetFor(null); setResetPw(""); setResetResult(null); };
  const copyPw = async () => {
    try { await navigator.clipboard.writeText(resetResult.new_password); toast.success(t("copied") || "Copied"); } catch { toast.error("Copy failed"); }
  };
  const [deleteFor, setDeleteFor] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const doDelete = async () => {
    if (!deleteFor) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/${deleteFor.id}`);
      toast.success("User deleted");
      setDeleteFor(null);
      load();
    } catch(er) { toast.error(errMsg(er)); }
    finally { setDeleting(false); }
  };
  useEffect(() => { if (user?.role === "district_admin") setForm(f=>({...f, district: user.district})); }, [user]);
  const canManage = (u) => user && u.id !== user.id ? (user.role === "super_admin" || u.created_by === user.id) : false;
  return (
    <div>
      <PageHeader title="Manage Users" subtitle={`${users.length} accounts`}/>
      <div className="p-4 md:p-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Role</TableHead><TableHead>Scope</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
            <TableBody>{users.map(u=>(
              <TableRow key={u.id}>
                <TableCell className="font-semibold">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.phone||"—"}</TableCell>
                <TableCell><Badge>{u.role}</Badge></TableCell>
                <TableCell className="text-xs">{u.district||"—"}{u.ac_name?` · ${u.ac_name}`:""}</TableCell>
                <TableCell>{u.is_active?"Active":"Inactive"}</TableCell>
                <TableCell>
                  {canManage(u) ? (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" data-testid={`bu-reset-pw-${u.id}`} onClick={()=>{setResetFor(u); setResetResult(null); setResetPw("");}}>
                        <Key className="w-3 h-3 mr-1"/>{t("resetPassword") || "Reset Password"}
                      </Button>
                      <Button size="sm" variant="destructive" data-testid={`bu-delete-user-${u.id}`} onClick={()=>setDeleteFor(u)}>
                        <Trash2 className="w-3 h-3 mr-1"/>{t("delete") || "Delete"}
                      </Button>
                    </div>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </div>
        <form onSubmit={submit} className="border border-border bg-card p-6 h-fit space-y-3">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground">Add User</h3>
          <div><Label>Name</Label><Input required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="bu-name"/></div>
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="bu-email"/></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} data-testid="bu-phone"/></div>
          <div><Label>Password</Label><Input type="password" required value={form.password} onChange={e=>setForm({...form, password:e.target.value})} data-testid="bu-pw"/></div>
          <div><Label>Role</Label>
            <Select value={form.role} onValueChange={v=>setForm({...form, role:v, ac_no:null, ac_name:""})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{allowedRoles.map(r=><SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
          {(form.role==="district_admin"||form.role==="assembly_admin") && <div><Label>District</Label>
            <Select value={form.district||"none"} onValueChange={v=>setForm({...form, district:v==="none"?"":v, ac_no:null})} disabled={user?.role==="district_admin"}>
              <SelectTrigger data-testid="bu-district"><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{districts.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>}
          {form.role==="assembly_admin" && <div><Label>Assembly</Label>
            <Select value={form.ac_no?String(form.ac_no):"none"} onValueChange={v=>{ if(v==="none"){setForm({...form, ac_no:null}); return;} setForm({...form, ac_no:parseInt(v)});}} disabled={!form.district}>
              <SelectTrigger data-testid="bu-ac"><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{acsFiltered.map(a=><SelectItem key={a.ac_no} value={String(a.ac_no)}>{a.ac_name}</SelectItem>)}</SelectContent></Select></div>}
          <Button type="submit" className="w-full" data-testid="bu-add">Create</Button>
        </form>
      </div>

      <Dialog open={!!resetFor} onOpenChange={(o)=>{ if(!o) closeReset(); }}>
        <DialogContent data-testid="bu-reset-pw-dialog">
          <DialogHeader><DialogTitle>{t("resetPassword") || "Reset Password"} — {resetFor?.name}</DialogTitle></DialogHeader>
          {!resetResult ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Enter a new password below, or leave blank to auto-generate a strong one.</p>
              <div>
                <Label>{t("newPassword") || "New Password"}</Label>
                <Input data-testid="bu-reset-pw-input" type="text" placeholder="(auto-generate)" value={resetPw} onChange={e=>setResetPw(e.target.value)}/>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm">Password reset for <b>{resetResult.email}</b>. Share this password securely — it will not be shown again.</p>
              <div className="border-2 border-accent bg-secondary p-4 flex items-center justify-between">
                <code data-testid="bu-reset-pw-result" className="text-lg font-mono font-bold tracking-wide select-all">{resetResult.new_password}</code>
                <Button size="sm" variant="outline" onClick={copyPw} data-testid="bu-copy-new-pw"><Copy className="w-3 h-3 mr-1"/>{t("copy") || "Copy"}</Button>
              </div>
            </div>
          )}
          <DialogFooter>
            {!resetResult
              ? <>
                  <Button variant="outline" onClick={closeReset}>{t("cancel") || "Cancel"}</Button>
                  <Button data-testid="bu-reset-pw-submit" onClick={doReset}>{t("resetPassword") || "Reset Password"}</Button>
                </>
              : <Button onClick={closeReset}>{t("close") || "Close"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteFor} onOpenChange={(o)=>{ if(!o) setDeleteFor(null); }}>
        <DialogContent data-testid="bu-delete-user-dialog">
          <DialogHeader><DialogTitle>{t("delete") || "Delete"} — {deleteFor?.name}</DialogTitle></DialogHeader>
          <p className="text-sm">Are you sure you want to delete <b>{deleteFor?.email}</b>? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDeleteFor(null)}>{t("cancel") || "Cancel"}</Button>
            <Button variant="destructive" disabled={deleting} data-testid="bu-delete-user-confirm" onClick={doDelete}>{deleting?"…":(t("delete") || "Delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProtectedBaithak({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-8">Loading...</div>;
  if (!user) return <Navigate to="/baithaks/login" replace state={{ from: loc.pathname }}/>;
  return <BaithakLayout>{children}</BaithakLayout>;
}

function BaithakLoginPage() {
  const { login, user } = useAuth();
  const { t, lang, setLang } = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const nextPath = loc.state?.from || "/baithaks";
  useEffect(() => { if (user) nav(nextPath, { replace: true }); }, [user, nav, nextPath]);
  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await login(email, password, "baithak"); toast.success("Welcome"); nav(nextPath, { replace: true }); }
    catch (er) { toast.error(errMsg(er)); }
    finally { setLoading(false); }
  };
  return (
    <div className={`min-h-screen grid lg:grid-cols-2 bg-background ${lang==="bn"?"font-bn":""}`}>
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 30%, rgba(212,175,55,0.3), transparent 50%)'}}/>
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 border-2 border-accent flex items-center justify-center bg-white"><CPIMLogo className="w-10 h-10"/></div>
            <span className="tracking-widest text-xs uppercase text-accent">{t("tileBaithakaccent")}</span>
          </div>
          <h1 className="mt-16 text-5xl lg:text-6xl font-black leading-tight tracking-tight">{t("tileBaithakTitle")}</h1>
          <p className="mt-6 text-lg opacity-85 max-w-md">{t("tileBaithakdesc")}</p>
        </div>
        <div className="relative z-10 text-xs uppercase tracking-widest opacity-70">© CPI(M) West Bengal</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="baithak-login-form">
          <div className="flex justify-between items-center">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Para Baithak · Sign In</span>
            <div className="flex gap-1 text-xs">
              <button type="button" data-testid="lang-en" onClick={()=>setLang("en")} className={`px-2 py-1 border ${lang==="en"?"bg-primary text-primary-foreground border-primary":"border-border"}`}>EN</button>
              <button type="button" data-testid="lang-bn" onClick={()=>setLang("bn")} className={`px-2 py-1 border ${lang==="bn"?"bg-primary text-primary-foreground border-primary":"border-border"}`}>বাং</button>
            </div>
          </div>
          <div>
            <h2 className="text-3xl font-bold tracking-tight">{t("tileBaithakTitle")}</h2>
            <p className="text-sm text-muted-foreground mt-2">{lang==="bn"?"শুধুমাত্র অনুমোদিত সংগঠকদের জন্য।":"Authorised organisers only."}</p>
          </div>
          <div className="space-y-4">
            <div><Label>{t("email")}</Label><Input data-testid="b-login-email" type="email" required value={email} onChange={e=>setEmail(e.target.value)}/></div>
            <div><Label>{t("password")}</Label><Input data-testid="b-login-password" type="password" required value={password} onChange={e=>setPassword(e.target.value)}/></div>
          </div>
          <Button data-testid="b-login-submit" type="submit" disabled={loading} className="w-full h-11">{loading?"...":t("login")}</Button>
          <div className="text-center space-x-3">
            <Link data-testid="b-forgot-pw-link" to="/forgot-password?module=baithak" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary underline underline-offset-4">{t("forgotPassword")}</Link>
            <span className="text-xs text-muted-foreground">·</span>
            <Link to="/" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary underline underline-offset-4">{t("backToPortal")}</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Protected + Language provider ---
function Protected({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-8">Loading...</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }}/>;
  return <AppLayout>{children}</AppLayout>;
}

function LanguageProvider({ children }) {
  const [lang, setLang] = useState(localStorage.getItem("lang_v2") || "bn");
  useEffect(() => { localStorage.setItem("lang_v2", lang); }, [lang]);
  const t = (k) => TR[lang][k] ?? k;
  return <LangCtx.Provider value={{ lang, setLang, t }}>{children}</LangCtx.Provider>;
}

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right"/>
          <Routes>
            <Route path="/" element={<LandingPortal/>}/>
            <Route path="/login" element={<LoginPage/>}/>
            <Route path="/baithaks/login" element={<BaithakLoginPage/>}/>
            <Route path="/forgot-password" element={<ForgotPasswordPage/>}/>
            <Route path="/reset-password/:token" element={<ResetPasswordPage/>}/>
            <Route path="/baithaks/reset-password/:token" element={<ResetPasswordPage/>}/>
            <Route path="/dashboard" element={<Protected><Dashboard/></Protected>}/>
            <Route path="/baithaks" element={<ProtectedBaithak><BaithakListPage/></ProtectedBaithak>}/>
            <Route path="/baithaks/new" element={<ProtectedBaithak><BaithakForm/></ProtectedBaithak>}/>
            <Route path="/baithaks/users" element={<ProtectedBaithak><BaithakUsersPage/></ProtectedBaithak>}/>
            <Route path="/baithaks/:id" element={<ProtectedBaithak><BaithakDetailPage/></ProtectedBaithak>}/>
            <Route path="/baithaks/:id/edit" element={<ProtectedBaithak><BaithakForm/></ProtectedBaithak>}/>
            <Route path="/influencers" element={<Protected><InfluencersPage/></Protected>}/>
            <Route path="/influencers/:id" element={<Protected><InfluencerForm/></Protected>}/>
            <Route path="/events" element={<Protected><EventsPage/></Protected>}/>
            <Route path="/events/:id" element={<Protected><EventForm/></Protected>}/>
            <Route path="/categories" element={<Protected><CategoriesPage/></Protected>}/>
            <Route path="/audit" element={<Protected><AuditPage/></Protected>}/>
            <Route path="/users" element={<Protected><UsersPage/></Protected>}/>
            <Route path="/surveys/admin" element={<Protected><SurveyAdminPage/></Protected>}/>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}
